import axios from 'axios';

// Normalize Persian / Arabic-Indic digits to English digits (for reliable server search)
const _PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const _ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
function normalizeDigits(value) {
  if (value === null || value === undefined) return value;
  const s = String(value);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const p = _PERSIAN_DIGITS.indexOf(ch);
    if (p !== -1) { out += String(p); continue; }
    const a = _ARABIC_INDIC_DIGITS.indexOf(ch);
    if (a !== -1) { out += String(a); continue; }
    out += ch;
  }
  return out.trim();
}

function resolveApiBaseUrl(rawValue) {
  const value = String(rawValue || '').trim();
  const hasWindow = typeof window !== 'undefined';
  const location = hasWindow ? window.location : null;
  const currentPort = location?.port || '';
  const currentProtocol = location?.protocol || 'http:';
  const currentHostname = location?.hostname || 'localhost';

  const shouldPreferDirectBackend = currentPort === '3000' || currentPort === '3001';

  // In many deployments the frontend is served on :3001 while Django API lives on :8001.
  // Prefer the direct backend URL in that case, unless the user explicitly configured another base URL.
  if (!value || value === 'undefined' || value === 'null' || value === '/api') {
    if (hasWindow && shouldPreferDirectBackend) {
      return `${currentProtocol}//${currentHostname}:8001/api`;
    }
    return '/api';
  }

  const trimmed = value.replace(/\/$/, '');
  if (trimmed.endsWith('/api')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return `${trimmed}/api`;
  if (trimmed.startsWith('/')) return trimmed;
  return `/${trimmed}`;
}

const API_URL = resolveApiBaseUrl(process.env.REACT_APP_API_URL);


const API_TIMEOUTS = Object.freeze({
  READ: Number(process.env.REACT_APP_API_READ_TIMEOUT_MS || 90000),
  WRITE: Number(process.env.REACT_APP_API_WRITE_TIMEOUT_MS || 120000),
  LOOKUP: Number(process.env.REACT_APP_API_LOOKUP_TIMEOUT_MS || 60000),
  REPORT: Number(process.env.REACT_APP_API_REPORT_TIMEOUT_MS || 900000),
  UPLOAD: 0,
});
const API_SLOW_REQUEST_MS = Number(process.env.REACT_APP_API_SLOW_REQUEST_MS || 4200);
const NETWORK_DIAGNOSTICS_KEY = 'damyar:network-diagnostics:v1';
const NETWORK_DIAGNOSTICS_LIMIT = 100;
const IDEMPOTENCY_STORAGE_PREFIX = 'damyar:idempotency:v1:';
const IDEMPOTENCY_REUSE_WINDOW_MS = 2 * 60 * 1000;
const SAFE_RETRY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 502, 503, 504]);
const RETRYABLE_NETWORK_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CONNECTION_RESET']);
const LONG_RUNNING_URL_MARKERS = [
  '/import-excel/', '/excel-import/', '/backfill/', '/backup', '/restore',
  '/export/', '/analytics/', '/reports/', '/pdf', '/xlsx', '/sync-from-project/',
];
const LOOKUP_URL_MARKERS = ['/options/', '/select-list/', '/select_list/', '/search/', '/lookup/'];
const IDEMPOTENCY_EXCLUDED_URL_MARKERS = ['/import-excel/', '/excel-import/', '/backfill/', '/backup', '/restore', '/sync-from-project/'];

function safeNow() {
  try { return Date.now(); } catch (error) { return 0; }
}

function randomRequestId(prefix = 'web') {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${safeNow().toString(36)}-${randomPart}`;
}

function simpleHash(value) {
  const input = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function signatureFingerprint(value) {
  const input = String(value || '');
  const reversed = input.split('').reverse().join('');
  return `${simpleHash(input)}-${input.length.toString(36)}-${simpleHash(reversed)}`;
}

function requestMethod(config) {
  return String(config?.method || 'get').toUpperCase();
}

function requestUrl(config) {
  return String(config?.url || '');
}

function isLongRunningRequest(config) {
  if (config?.timeout === 0 || config?._damyarLongRunning === true) return true;
  const url = requestUrl(config).toLowerCase();
  if (isExcelImportRequest(config)) return true;
  if (String(config?.responseType || '').toLowerCase() === 'blob') return true;
  return LONG_RUNNING_URL_MARKERS.some((marker) => url.includes(marker));
}

function resolveRequestTimeout(config) {
  const configured = Number(config?.timeout);
  if (configured === 0) return 0;
  // Preserve explicit endpoint overrides such as 900000ms.
  if (Number.isFinite(configured) && configured > 0 && configured !== API_TIMEOUTS.READ) return configured;
  if (isLongRunningRequest(config)) return API_TIMEOUTS.REPORT;
  if (!SAFE_RETRY_METHODS.has(requestMethod(config))) return API_TIMEOUTS.WRITE;
  const url = requestUrl(config).toLowerCase();
  if (LOOKUP_URL_MARKERS.some((marker) => url.includes(marker))) return API_TIMEOUTS.LOOKUP;
  return API_TIMEOUTS.READ;
}

function isIdempotencyExcludedRequest(config) {
  const url = requestUrl(config).toLowerCase();
  if (IDEMPOTENCY_EXCLUDED_URL_MARKERS.some((marker) => url.includes(marker))) return true;
  const data = config?.data;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true;
  if (typeof File !== 'undefined' && data instanceof File) return true;
  return false;
}

function requestBodyForSignature(data) {
  if (data === null || data === undefined) return '';
  if (typeof FormData !== 'undefined' && data instanceof FormData) return null;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return null;
  if (typeof File !== 'undefined' && data instanceof File) return null;
  if (typeof data === 'string') return data;
  try { return stableStringify(data); } catch (error) { return String(data); }
}

function idempotencySignature(config) {
  const method = requestMethod(config);
  if (SAFE_RETRY_METHODS.has(method) || isIdempotencyExcludedRequest(config)) return null;
  const body = requestBodyForSignature(config?.data);
  if (body === null) return null;
  return `${getAuthCacheNamespace()}|${method}|${requestUrl(config)}|${stableStringify(config?.params || {})}|${body}`;
}

function getIdempotencyStorageKey(signature) {
  return `${IDEMPOTENCY_STORAGE_PREFIX}${signatureFingerprint(signature)}`;
}

function loadOrCreateIdempotencyKey(config) {
  const signature = idempotencySignature(config);
  if (!signature) return null;
  const storage = getBrowserStorage();
  const fingerprint = signatureFingerprint(signature);
  const storageKey = getIdempotencyStorageKey(signature);
  const now = safeNow();
  if (storage) {
    try {
      const parsed = JSON.parse(storage.getItem(storageKey) || 'null');
      if (parsed?.fingerprint === fingerprint && Number(parsed?.expiresAt || 0) > now && parsed?.key) {
        config._damyarIdempotencyStorageKey = storageKey;
        config._damyarIdempotencySignature = fingerprint;
        return parsed.key;
      }
    } catch (error) {
      try { storage.removeItem(storageKey); } catch (storageError) { /* ignore */ }
    }
  }
  const key = randomRequestId('idem');
  if (storage) {
    try {
      storage.setItem(storageKey, JSON.stringify({
        key,
        fingerprint,
        createdAt: now,
        expiresAt: now + IDEMPOTENCY_REUSE_WINDOW_MS,
      }));
    } catch (error) {
      // In-memory request metadata still keeps the key for interceptor retries.
    }
  }
  config._damyarIdempotencyStorageKey = storageKey;
  config._damyarIdempotencySignature = fingerprint;
  return key;
}

function clearIdempotencyRecord(config) {
  const storage = getBrowserStorage();
  const key = config?._damyarIdempotencyStorageKey;
  if (!storage || !key) return;
  try { storage.removeItem(key); } catch (error) { /* ignore */ }
}

function getHeaderValue(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return value;
  }
  return headers[name] ?? headers[name.toLowerCase()] ?? headers[name.toUpperCase()] ?? null;
}

function parseOptionalNumber(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getRequestDurationMs(responseLike) {
  const response = responseLike?.response || responseLike;
  const config = responseLike?.config || response?.config || {};
  const startedAt = Number(config?._damyarOverallStartedAt || config?._damyarStartedAt || 0);
  if (startedAt > 0) return Math.max(0, safeNow() - startedAt);
  return parseOptionalNumber(getHeaderValue(response?.headers, 'x-response-time-ms'));
}

export function isTimeoutError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT' || message.includes('timeout') || message.includes('timed out');
}

export function classifyRequestError(error) {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.code || '').toUpperCase();
  const hasTransportContext = Boolean(error?.isAxiosError || error?.config || error?.request || code);
  if (error?.__CANCEL__ || code === 'ERR_CANCELED') return 'cancelled';
  if (hasTransportContext && isTimeoutError(error)) return 'timeout';
  if ([502, 504, 524].includes(status)) return 'gateway_timeout';
  if ([429, 503].includes(status)) return 'server_busy';
  if (hasTransportContext && (!error?.response || RETRYABLE_NETWORK_CODES.has(code))) return 'network';
  if (status >= 500) return 'server';
  if (status === 409 && error?.response?.data?.code === 'idempotency_in_progress') return 'idempotency_in_progress';
  return 'http';
}

function readRetryAfterMs(error) {
  const raw = getHeaderValue(error?.response?.headers, 'retry-after');
  if (!raw) return 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const at = Date.parse(String(raw));
  return Number.isFinite(at) ? Math.max(0, at - safeNow()) : 0;
}

function retryDelayMs(error, retryCount) {
  const serverDelay = readRetryAfterMs(error);
  if (serverDelay > 0) return Math.min(serverDelay, 10000);
  const base = Math.min(700 * (2 ** Math.max(0, retryCount - 1)), 6000);
  return Math.round(base + Math.random() * 350);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function appendNetworkDiagnostic(entry) {
  const storage = getBrowserStorage();
  if (!storage) return;
  try {
    const current = JSON.parse(storage.getItem(NETWORK_DIAGNOSTICS_KEY) || '[]');
    const rows = Array.isArray(current) ? current : [];
    rows.push({ at: new Date().toISOString(), ...entry });
    storage.setItem(NETWORK_DIAGNOSTICS_KEY, JSON.stringify(rows.slice(-NETWORK_DIAGNOSTICS_LIMIT)));
  } catch (error) {
    // Diagnostics must never break a business request.
  }
}

export function getNetworkDiagnostics() {
  const storage = getBrowserStorage();
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(NETWORK_DIAGNOSTICS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export function clearNetworkDiagnostics() {
  const storage = getBrowserStorage();
  if (!storage) return;
  try { storage.removeItem(NETWORK_DIAGNOSTICS_KEY); } catch (error) { /* ignore */ }
}

function recordRequestResult(responseLike, outcome, errorKind = null) {
  const response = responseLike?.response || responseLike;
  const config = responseLike?.config || response?.config || {};
  const durationMs = getRequestDurationMs(responseLike);
  const serverDurationMs = parseOptionalNumber(getHeaderValue(response?.headers, 'x-response-time-ms'));
  const proxyUpstreamMs = parseOptionalNumber(getHeaderValue(response?.headers, 'x-proxy-upstream-time-ms'));
  const proxyConnectRaw = getHeaderValue(response?.headers, 'x-proxy-connect-time-ms');
  const proxyConnectMs = parseOptionalNumber(proxyConnectRaw);
  const entry = {
    outcome,
    error_kind: errorKind,
    method: requestMethod(config),
    url: requestUrl(config),
    status: Number(response?.status || 0) || null,
    duration_ms: durationMs === null ? null : Math.round(durationMs),
    server_duration_ms: serverDurationMs,
    proxy_upstream_ms: proxyUpstreamMs,
    proxy_connect_ms: proxyConnectMs,
    proxy_connection_reused: String(proxyConnectRaw || '').toLowerCase() === 'reused',
    timeout_ms: Number(config?._damyarTimeoutMs || config?.timeout || 0) || null,
    retry_count: Number(config?._damyarRetryCount || 0),
    client_request_id: config?._damyarClientRequestId || null,
    request_id: getResponseRequestId(responseLike),
  };
  if (outcome !== 'success' || Number(durationMs || 0) >= API_SLOW_REQUEST_MS) {
    appendNetworkDiagnostic(entry);
  }
  return entry;
}

const API_CACHE_VERSION = 'v2';
const API_CACHE_PREFIX = `damyar:api-cache:${API_CACHE_VERSION}:`;
const API_CACHE_INDEX_PREFIX = `damyar:api-cache:index:${API_CACHE_VERSION}:`;
const API_CACHE_MAX_ENTRIES = 60;
const API_CACHE_MAX_PAYLOAD_BYTES = 250 * 1024;
const CACHE_TTL = {
  SHORT: 60 * 1000,
  MEDIUM: 3 * 60 * 1000,
  LONG: 10 * 60 * 1000,
};

const apiMemoryCache = new Map();
const pendingCachedGets = new Map();
const RECENT_WRITE_REFRESH_WINDOW_MS = 12000;
let recentSuccessfulWrite = null;

function currentPagePath() {
  try {
    return typeof window !== 'undefined' ? window.location.pathname : '';
  } catch (error) {
    return '';
  }
}

function rememberSuccessfulWrite(response) {
  recentSuccessfulWrite = {
    at: Date.now(),
    pagePath: currentPagePath(),
    url: String(response?.config?.url || ''),
    method: String(response?.config?.method || '').toUpperCase(),
    requestId: getResponseRequestId(response),
  };
}

function recentWriteContextForRefreshError(error) {
  const method = String(error?.config?.method || 'get').toUpperCase();
  if (method !== 'GET' || !recentSuccessfulWrite) return null;
  const ageMs = Date.now() - Number(recentSuccessfulWrite.at || 0);
  if (ageMs < 0 || ageMs > RECENT_WRITE_REFRESH_WINDOW_MS) return null;
  if (recentSuccessfulWrite.pagePath && recentSuccessfulWrite.pagePath !== currentPagePath()) return null;
  return { ...recentSuccessfulWrite, ageMs };
}

function annotateRefreshErrorAfterWrite(error) {
  const context = recentWriteContextForRefreshError(error);
  if (!context) return null;
  const requestId = getResponseRequestId(error);
  const tracking = requestId ? ` شناسه پیگیری تازه‌سازی: ${requestId}.` : '';
  const message = `اطلاعات با موفقیت ذخیره شد، اما تازه‌سازی فهرست صفحه انجام نشد.${tracking} صفحه را یک‌بار تازه‌سازی کنید.`;
  error._damyarAfterSuccessfulWrite = { ...context, requestId, message };
  if (error.response) {
    const originalData = error.response.data;
    error.response.data = {
      ...(originalData && typeof originalData === 'object' && !Array.isArray(originalData) ? originalData : {}),
      detail: message,
      code: 'post_write_refresh_failed',
      request_id: requestId || undefined,
      write_saved: true,
      refresh_failed: true,
    };
  }
  return error._damyarAfterSuccessfulWrite;
}

function getBrowserStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function getAuthCacheNamespace() {
  const storage = getBrowserStorage();
  if (!storage) return 'guest';
  try {
    const raw = storage.getItem('auth-storage');
    if (!raw) return 'guest';
    const parsed = JSON.parse(raw);
    const state = parsed?.state || parsed;
    const user = state?.user;
    const userKey = user?.id || user?.username || user?.employee_code || user?.full_name;
    if (!userKey) return 'guest';
    return `user:${String(userKey)}`;
  } catch (error) {
    return 'guest';
  }
}

function getCacheIndexKey(namespace = getAuthCacheNamespace()) {
  return `${API_CACHE_INDEX_PREFIX}${namespace}`;
}

function sortObjectDeep(value) {
  if (Array.isArray(value)) {
    return value.map((item) => sortObjectDeep(item));
  }
  if (value && typeof value === 'object' && !(value instanceof Date) && !(value instanceof File) && !(value instanceof Blob)) {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortObjectDeep(value[key]);
        return acc;
      }, {});
  }
  return value;
}

function stableStringify(value) {
  try {
    return JSON.stringify(sortObjectDeep(value ?? null));
  } catch (error) {
    return JSON.stringify(String(value ?? ''));
  }
}

function makeCacheKey(url, params, customKey) {
  const namespace = getAuthCacheNamespace();
  const rawKey = customKey || `${url}::${stableStringify(params || {})}`;
  return `${API_CACHE_PREFIX}${namespace}:${rawKey}`;
}

function cloneCachedResponse(config, entry) {
  return {
    data: entry.data,
    status: entry.status || 200,
    statusText: entry.statusText || 'OK',
    headers: entry.headers || {},
    config,
    request: null,
    fromCache: true,
    cached: true,
  };
}

function readCacheIndex(namespace = getAuthCacheNamespace()) {
  const storage = getBrowserStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(getCacheIndexKey(namespace));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeCacheIndex(entries, namespace = getAuthCacheNamespace()) {
  const storage = getBrowserStorage();
  if (!storage) return;
  try {
    storage.setItem(getCacheIndexKey(namespace), JSON.stringify(entries));
  } catch (error) {
    // ignore storage write failures
  }
}

function removeCacheEntry(cacheKey, namespace = getAuthCacheNamespace()) {
  apiMemoryCache.delete(cacheKey);
  const storage = getBrowserStorage();
  if (storage) {
    try {
      storage.removeItem(cacheKey);
    } catch (error) {
      // ignore storage removal failures
    }
  }
  const nextIndex = readCacheIndex(namespace).filter((entry) => entry?.key !== cacheKey);
  writeCacheIndex(nextIndex, namespace);
}

function pruneCache(namespace = getAuthCacheNamespace()) {
  const storage = getBrowserStorage();
  const now = Date.now();
  let index = readCacheIndex(namespace).filter((entry) => entry?.key);
  index = index.filter((entry) => {
    const isExpired = !entry?.expiresAt || entry.expiresAt <= now;
    if (isExpired) {
      removeCacheEntry(entry.key, namespace);
      return false;
    }
    return true;
  });

  if (index.length <= API_CACHE_MAX_ENTRIES) {
    writeCacheIndex(index, namespace);
    return;
  }

  const sorted = [...index].sort((a, b) => Number(b.storedAt || 0) - Number(a.storedAt || 0));
  const keepKeys = new Set(sorted.slice(0, API_CACHE_MAX_ENTRIES).map((entry) => entry.key));
  index.forEach((entry) => {
    if (!keepKeys.has(entry.key)) {
      removeCacheEntry(entry.key, namespace);
    }
  });
  if (storage) {
    const kept = sorted.filter((entry) => keepKeys.has(entry.key));
    writeCacheIndex(kept, namespace);
  }
}

function readCachedEntry(cacheKey) {
  const inMemory = apiMemoryCache.get(cacheKey);
  const now = Date.now();
  if (inMemory) {
    if (inMemory.expiresAt > now) return inMemory;
    apiMemoryCache.delete(cacheKey);
  }

  const storage = getBrowserStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt <= now) {
      removeCacheEntry(cacheKey);
      return null;
    }
    apiMemoryCache.set(cacheKey, parsed);
    return parsed;
  } catch (error) {
    removeCacheEntry(cacheKey);
    return null;
  }
}

function canPersistResponse(response, ttlMs) {
  if (!ttlMs || ttlMs <= 0) return false;
  if (response?.config?.responseType && response.config.responseType !== 'json') return false;
  if (response?.data === undefined) return false;
  try {
    const serialized = JSON.stringify(response.data);
    if (!serialized) return false;
    return serialized.length <= API_CACHE_MAX_PAYLOAD_BYTES;
  } catch (error) {
    return false;
  }
}

function persistCachedResponse(cacheKey, response, ttlMs) {
  const storage = getBrowserStorage();
  const namespace = getAuthCacheNamespace();
  const now = Date.now();
  const entry = {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: {},
    storedAt: now,
    expiresAt: now + ttlMs,
  };

  apiMemoryCache.set(cacheKey, entry);

  if (!storage) return;
  try {
    storage.setItem(cacheKey, JSON.stringify(entry));
    const index = readCacheIndex(namespace).filter((item) => item?.key !== cacheKey);
    index.push({
      key: cacheKey,
      storedAt: entry.storedAt,
      expiresAt: entry.expiresAt,
    });
    writeCacheIndex(index, namespace);
    pruneCache(namespace);
  } catch (error) {
    removeCacheEntry(cacheKey, namespace);
  }
}

export function clearBrowserApiCache(namespace = getAuthCacheNamespace()) {
  const storage = getBrowserStorage();
  const prefix = `${API_CACHE_PREFIX}${namespace}:`;

  Array.from(apiMemoryCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      apiMemoryCache.delete(key);
    }
  });

  if (!storage) return;
  const index = readCacheIndex(namespace);
  index.forEach((entry) => {
    if (!entry?.key) return;
    try {
      storage.removeItem(entry.key);
    } catch (error) {
      // ignore storage removal failures
    }
  });
  writeCacheIndex([], namespace);
}

export async function cachedGet(url, config = {}) {
  const { cache, ...axiosConfig } = config || {};

  const ttlMs = typeof cache === 'number' ? cache : cache?.ttl;
  if (!ttlMs || ttlMs <= 0) {
    return api.get(url, axiosConfig);
  }

  const cacheKey = makeCacheKey(url, axiosConfig?.params, cache?.key);
  const cachedEntry = readCachedEntry(cacheKey);
  const effectiveConfig = {
    ...axiosConfig,
    url,
    method: 'get',
  };

  if (cachedEntry) {
    return cloneCachedResponse(effectiveConfig, cachedEntry);
  }

  if (pendingCachedGets.has(cacheKey)) {
    return pendingCachedGets.get(cacheKey);
  }

  const requestPromise = api.get(url, axiosConfig)
    .then((response) => {
      if (canPersistResponse(response, ttlMs)) {
        persistCachedResponse(cacheKey, response, ttlMs);
      }
      return response;
    })
    .finally(() => {
      pendingCachedGets.delete(cacheKey);
    });

  pendingCachedGets.set(cacheKey, requestPromise);
  return requestPromise;
}


const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUTS.READ,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
  },
});

export function setAuthHeader(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

export function clearStoredAuthState() {
  clearBrowserApiCache();
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth-storage');
  setAuthHeader(null);
}


export function extractListData(response) {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function normalizeCowSelectOption(item) {
  if (!item || typeof item !== 'object') return item;
  const normalized = { ...item };
  normalized.id = item.id;
  normalized.ear_tag = item.ear_tag ?? item.label ?? '';
  normalized.veterinary_number = item.veterinary_number ?? '';
  normalized.chkno = item.chkno ?? null;
  normalized.rno = item.rno ?? '';
  normalized.name = item.name ?? '';
  normalized.sex = item.sex ?? null;
  normalized.sex_display = item.sex_display ?? '';
  normalized.status = item.status ?? null;
  normalized.status_display = item.status_display ?? item.operational_status_display ?? '';
  normalized.is_active = item.is_active ?? true;
  normalized.baharband = item.baharband ?? null;
  normalized.baharband_code = item.baharband_code ?? '';
  normalized.pregnancy_ai_date = item.pregnancy_ai_date ?? null;
  normalized.last_service_date = item.last_service_date ?? null;
  normalized.pregnancy_days = item.pregnancy_days ?? null;
  normalized.mother = item.mother ?? null;
  normalized.mother_display = item.mother_display ?? item.mother_ear_tag ?? '';
  normalized.sire = item.sire ?? null;
  normalized.sire_display = item.sire_display ?? item.sire_ear_tag ?? '';
  if (!normalized.label) {
    const parts = [normalized.ear_tag];
    if (normalized.veterinary_number) parts.push(`دامپزشکی:${normalized.veterinary_number}`);
    if (normalized.rno) parts.push(`RNO:${normalized.rno}`);
    if (normalized.name) parts.push(normalized.name);
    if (normalized.baharband_code) parts.push(`(${normalized.baharband_code})`);
    normalized.label = parts.filter(Boolean).join(' - ').trim();
  }
  return normalized;
}

let cowsSelectListAvailability = null;

function shouldUseDedicatedCowsSelectList() {
  return cowsSelectListAvailability !== false;
}

function markDedicatedCowsSelectListAvailable(isAvailable) {
  cowsSelectListAvailability = isAvailable;
}

function flattenErrorMessages(value) {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenErrorMessages(item));
  }
  if (typeof value === 'object') {
    return Object.values(value).flatMap((item) => flattenErrorMessages(item));
  }
  return [];
}

export function getResponseRequestId(responseLike) {
  const response = responseLike?.response || responseLike;
  const headers = response?.headers || {};
  return (
    response?.data?.request_id ||
    headers['x-request-id'] ||
    headers['X-Request-ID'] ||
    null
  );
}

export function wasWriteRolledBack(responseLike) {
  const response = responseLike?.response || responseLike;
  const headers = response?.headers || {};
  const value = headers['x-write-rolled-back'] ?? headers['X-Write-Rolled-Back'];
  return String(value ?? '') === '1';
}

export function getWriteAtomicMode(responseLike) {
  const response = responseLike?.response || responseLike;
  const headers = response?.headers || {};
  const value = headers['x-write-atomic'] ?? headers['X-Write-Atomic'];
  const normalized = String(value ?? '').trim().toUpperCase();
  return normalized === 'ENABLED' || normalized === 'BYPASS' ? normalized : null;
}

export function getErrorMessage(error, fallback = 'خطا در دریافت اطلاعات') {
  if (error?._damyarAfterSuccessfulWrite?.message) return error._damyarAfterSuccessfulWrite.message;
  const kind = classifyRequestError(error);
  const durationMs = getRequestDurationMs(error);
  const timeoutMs = Number(error?.config?._damyarTimeoutMs || error?.config?.timeout || 0);
  const durationText = Number.isFinite(durationMs) && durationMs !== null
    ? ` پس از ${(durationMs / 1000).toFixed(durationMs >= 10000 ? 0 : 1)} ثانیه`
    : '';
  if (kind === 'timeout') {
    const limitText = timeoutMs > 0 ? ` (مهلت ${Math.round(timeoutMs / 1000)} ثانیه)` : '';
    return `پاسخ سرور${durationText} در مهلت تعیین‌شده دریافت نشد${limitText}. اتصال و بار سرور را بررسی کنید.`;
  }
  if (kind === 'gateway_timeout') {
    return `پروکسی یا درگاه ارتباطی${durationText} پاسخ کامل از بک‌اند دریافت نکرد. درخواست دوباره با شناسه پیگیری کنترل می‌شود.`;
  }
  if (kind === 'server_busy') {
    return `سرور در حال حاضر شلوغ است${durationText}. سامانه به‌صورت کنترل‌شده دوباره تلاش می‌کند.`;
  }
  if (kind === 'network') {
    return `ارتباط مرورگر با سرور${durationText} قطع شد یا پاسخ شبکه دریافت نشد. اتصال شبکه و سرویس دام‌یار را بررسی کنید.`;
  }
  if (kind === 'idempotency_in_progress') {
    return 'همین درخواست قبلاً ارسال شده و هنوز روی سرور در حال پردازش است؛ برای جلوگیری از ثبت تکراری کمی صبر کنید.';
  }
  const data = error?.response?.data ?? error?.data ?? error;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    const first = flattenErrorMessages(data.non_field_errors)[0];
    if (first) return first;
  }
  const flattened = flattenErrorMessages(data);
  return flattened[0] || fallback;
}

export function extractFieldErrors(error, fieldMap = {}) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') return {};

  const resolved = {};
  for (const [targetField, candidates] of Object.entries(fieldMap)) {
    const keys = Array.isArray(candidates) ? candidates : [candidates];
    for (const key of keys) {
      const messages = flattenErrorMessages(data?.[key]);
      if (messages.length) {
        resolved[targetField] = messages.join(' ');
        break;
      }
    }
  }
  return resolved;
}

export function extractResponseData(response, fallback = null) {
  if (response?.data === undefined) return fallback;
  return response.data;
}

export async function safeRequest(requestOrFactory, options = {}) {
  const {
    fallbackData = null,
    fallbackError = 'خطا در دریافت اطلاعات',
  } = options;

  try {
    const response = typeof requestOrFactory === 'function'
      ? await requestOrFactory()
      : await requestOrFactory;

    const data = extractResponseData(response, fallbackData);
    return {
      ok: true,
      response,
      data,
      list: extractListData(response),
      error: null,
      errorMessage: '',
    };
  } catch (error) {
    const fallbackList = Array.isArray(fallbackData) ? fallbackData : [];
    return {
      ok: false,
      response: error?.response ?? null,
      data: fallbackData,
      list: fallbackList,
      error,
      errorMessage: getErrorMessage(error, fallbackError),
    };
  }
}

export async function safeParallel(requestMap = {}) {
  const entries = Object.entries(requestMap);
  const settled = await Promise.all(entries.map(async ([key, config]) => {
    const normalized = typeof config === 'function' || config?.then
      ? { request: config }
      : (config || {});
    const result = await safeRequest(normalized.request, normalized);
    return [key, result];
  }));
  return Object.fromEntries(settled);
}

export function getSafeData(result, fallback = null) {
  if (result?.data === undefined) return fallback;
  return result.data ?? fallback;
}

export function getSafeList(result, fallback = []) {
  if (Array.isArray(result?.list)) return result.list;
  return fallback;
}

export function getFailedRequestLabels(results, labels = {}) {
  return Object.entries(labels)
    .filter(([key]) => results?.[key] && !results[key].ok)
    .map(([, label]) => label);
}

export function buildPartialLoadMessage(labels = [], prefix = 'بخشی از اطلاعات بارگیری نشد') {
  if (!labels.length) return prefix;
  return `${prefix}: ${labels.join('، ')}`;
}

export function withXlsxExportParams(params = {}) {
  return { ...(params || {}), export: 'xlsx' };
}

export function withPdfExportParams(params = {}) {
  return { ...(params || {}), export: 'pdf' };
}

export function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const escaped = name.replace(/[-[\]{}()*+?.,^$|#\s]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function isUnsafeMethod(method) {
  const normalized = String(method || 'get').toUpperCase();
  return !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(normalized);
}

let csrfPromise = null;

export async function ensureCsrfCookie() {
  const existing = readCookie('csrftoken');
  if (existing) return existing;
  if (csrfPromise) return csrfPromise;

  csrfPromise = axios.get(`${API_URL}/auth/csrf/`, {
    withCredentials: true,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
  })
    .then(() => readCookie('csrftoken'))
    .finally(() => {
      csrfPromise = null;
    });

  return csrfPromise;
}

let refreshPromise = null;

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return null;
  }

  refreshPromise = axios.post(`${API_URL}/token/refresh/`, {
    refresh: refreshToken,
  }, {
    withCredentials: true,
  })
    .then((response) => {
      const { access, refresh } = response.data || {};
      if (access) {
        localStorage.setItem('access_token', access);
        setAuthHeader(access);
      }
      if (refresh) localStorage.setItem('refresh_token', refresh);
      return access || null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

function isAuthEndpointUrl(url) {
  const requestUrl = String(url || '');
  return (
    requestUrl.includes('/auth/login/') ||
    requestUrl.includes('/token/refresh/') ||
    requestUrl.includes('/auth/csrf/')
  );
}


function isExcelImportRequest(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  const url = String(config.url || '');
  return method !== 'get' && config.data instanceof FormData && /\/import-excel\/?(?:$|[?#])/.test(url);
}

function getFormDataFileName(formData) {
  try {
    const file = formData?.get?.('file') || formData?.get?.('excel');
    return file?.name || '';
  } catch {
    return '';
  }
}

function dispatchExcelImportEvent(detail) {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('damyar:excel-import-progress', { detail }));
    }
  } catch {
    // UI progress is best-effort; never break API calls.
  }
}

// Request interceptor - add auth token
api.interceptors.request.use(
  async (config) => {
    const headers = config.headers || {};
    const now = safeNow();
    if (!config._damyarOverallStartedAt) config._damyarOverallStartedAt = now;
    config._damyarStartedAt = now;
    config._damyarAttemptStartedAt = now;
    config._damyarClientRequestId = config._damyarClientRequestId || randomRequestId('req');
    config.timeout = resolveRequestTimeout(config);
    config._damyarTimeoutMs = config.timeout;
    config._damyarRetryCount = Number(config._damyarRetryCount || 0);
    config._damyarMaxRetries = Number.isFinite(Number(config._damyarMaxRetries))
      ? Number(config._damyarMaxRetries)
      : (SAFE_RETRY_METHODS.has(requestMethod(config)) ? (isLongRunningRequest(config) ? 1 : 2) : 1);
    headers['X-Client-Request-ID'] = config._damyarClientRequestId;
    headers['X-Client-Timeout-Ms'] = String(config.timeout || 0);

    if (!SAFE_RETRY_METHODS.has(requestMethod(config)) && !isIdempotencyExcludedRequest(config)) {
      const idempotencyKey = config._damyarIdempotencyKey || loadOrCreateIdempotencyKey(config);
      if (idempotencyKey) {
        config._damyarIdempotencyKey = idempotencyKey;
        headers['X-Idempotency-Key'] = idempotencyKey;
      }
    }

    config.withCredentials = true;
    if (config.data instanceof FormData && headers) {
      delete headers['Content-Type'];
    }

    if (isUnsafeMethod(config.method) && !isAuthEndpointUrl(config.url)) {
      try {
        await ensureCsrfCookie();
      } catch (error) {
        // Continue; some deployments rely only on JWT and do not require CSRF cookies.
      }
      const csrfToken = readCookie('csrftoken');
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
        config.withCredentials = true;
      }
    }

    let token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!token && refreshToken && !isAuthEndpointUrl(config.url)) {
      try {
        token = await refreshAccessToken();
      } catch (error) {
        clearStoredAuthState();
        token = null;
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (isExcelImportRequest(config)) {
      const uploadId = `excel-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const originalProgress = config.onUploadProgress;
      const fileName = getFormDataFileName(config.data);
      config._damyarExcelImport = { id: uploadId, url: config.url, fileName };
      dispatchExcelImportEvent({
        id: uploadId,
        url: config.url,
        file_name: fileName,
        phase: 'uploading',
        status: 'running',
        upload_percent: 0,
        progress_percent: 0,
        message: 'در حال آپلود فایل اکسل...',
      });
      config.onUploadProgress = (progressEvent) => {
        if (typeof originalProgress === 'function') {
          originalProgress(progressEvent);
        }
        const total = Number(progressEvent?.total || 0);
        const loaded = Number(progressEvent?.loaded || 0);
        const percent = total > 0 ? Math.round((loaded * 100) / total) : 0;
        dispatchExcelImportEvent({
          id: uploadId,
          url: config.url,
          file_name: fileName,
          phase: percent >= 100 ? 'processing' : 'uploading',
          status: 'running',
          upload_percent: percent,
          progress_percent: Math.min(percent, 95),
          message: percent >= 100 ? 'فایل آپلود شد؛ سرور در حال پردازش اکسل است...' : 'در حال آپلود فایل اکسل...',
        });
      };
    }

    config.headers = headers;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => {
    const method = response?.config?.method;
    const excelImport = response?.config?._damyarExcelImport;
    if (excelImport) {
      dispatchExcelImportEvent({
        id: excelImport.id,
        url: excelImport.url,
        file_name: excelImport.fileName,
        phase: 'done',
        status: 'succeeded',
        upload_percent: 100,
        progress_percent: 100,
        job_id: response?.headers?.['x-import-job-id'] || response?.headers?.['X-Import-Job-ID'],
        import_batch: response?.headers?.['x-import-batch'] || response?.headers?.['X-Import-Batch'] || response?.data?.import_batch,
        result: response?.data,
        message: 'ایمپورت اکسل کامل شد.',
      });
    }
    if (isUnsafeMethod(method) && !isAuthEndpointUrl(response?.config?.url)) {
      clearBrowserApiCache();
      rememberSuccessfulWrite(response);
    }
    if (response?.data?.representation_degraded === true) {
      try {
        window.dispatchEvent(new CustomEvent('damyar:write-response-warning', {
          detail: {
            message: response?.data?.detail || 'اطلاعات ذخیره شد، اما پاسخ کامل سرور قابل نمایش نبود.',
            requestId: getResponseRequestId(response),
          },
        }));
      } catch (eventError) {
        // A diagnostic UI warning must never break the successful request.
      }
    }
    if (isUnsafeMethod(method)) {
      clearIdempotencyRecord(response?.config);
    }
    const requestDiagnostic = recordRequestResult(response, 'success');
    if (Number(requestDiagnostic?.duration_ms || 0) >= API_SLOW_REQUEST_MS) {
      try {
        window.dispatchEvent(new CustomEvent('damyar:slow-request', { detail: requestDiagnostic }));
      } catch (eventError) {
        // Slow-request telemetry is best-effort.
      }
    }
    const partialSerialization = response?.headers?.['x-partial-serialization'] || response?.headers?.['X-Partial-Serialization'];
    if (String(partialSerialization || '') === '1') {
      try {
        const degradedCount = response?.headers?.['x-degraded-record-count'] || response?.headers?.['X-Degraded-Record-Count'] || 1;
        window.dispatchEvent(new CustomEvent('damyar:write-response-warning', {
          detail: {
            message: `بخشی از داده‌های قدیمی صفحه ناسازگار است و ${degradedCount} رکورد به‌صورت خلاصه نمایش داده شد. شناسه پیگیری: ${getResponseRequestId(response) || '-'}`,
            requestId: getResponseRequestId(response),
          },
        }));
      } catch (eventError) {
        // keep successful list/detail responses usable
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const errorKind = classifyRequestError(error);
    const method = requestMethod(originalRequest);
    const retryCount = Number(originalRequest._damyarRetryCount || 0);
    const maxRetries = Number(originalRequest._damyarMaxRetries || 0);
    const statusCode = Number(error?.response?.status || 0);
    const isAuthEndpointForRetry = isAuthEndpointUrl(originalRequest.url);
    const excelImportForRetry = originalRequest?._damyarExcelImport;
    const isIdempotencyInProgress = errorKind === 'idempotency_in_progress';
    const safeRetry = SAFE_RETRY_METHODS.has(method)
      && !isAuthEndpointForRetry
      && !excelImportForRetry
      && retryCount < maxRetries
      && (RETRYABLE_STATUS_CODES.has(statusCode) || ['timeout', 'network', 'gateway_timeout', 'server_busy'].includes(errorKind));
    const idempotentWriteRetry = !SAFE_RETRY_METHODS.has(method)
      && Boolean(originalRequest?._damyarIdempotencyKey)
      && !excelImportForRetry
      && (
        (isIdempotencyInProgress && retryCount < Math.max(maxRetries, 4))
        || (retryCount < maxRetries && ['timeout', 'network', 'gateway_timeout', 'server_busy'].includes(errorKind))
      );

    if ((safeRetry || idempotentWriteRetry) && String(error?.code || '').toUpperCase() !== 'ERR_CANCELED') {
      originalRequest._damyarRetryCount = retryCount + 1;
      const delayMs = retryDelayMs(error, originalRequest._damyarRetryCount);
      const diagnostic = recordRequestResult(error, 'retrying', errorKind);
      try {
        window.dispatchEvent(new CustomEvent('damyar:request-retry', {
          detail: {
            ...diagnostic,
            delay_ms: delayMs,
            attempt: originalRequest._damyarRetryCount,
            max_attempts: isIdempotencyInProgress ? Math.max(maxRetries, 4) : maxRetries,
          },
        }));
      } catch (eventError) {
        // Retry continues even when the UI event cannot be dispatched.
      }
      await sleep(delayMs);
      return api(originalRequest);
    }

    const postWriteRefreshContext = annotateRefreshErrorAfterWrite(error);
    const finalDiagnostic = recordRequestResult(error, 'failed', errorKind);
    const uncertainWriteResult = !SAFE_RETRY_METHODS.has(method)
      && Boolean(originalRequest?._damyarIdempotencyKey)
      && ['timeout', 'network', 'gateway_timeout', 'idempotency_in_progress'].includes(errorKind);
    if (!uncertainWriteResult) {
      clearIdempotencyRecord(originalRequest);
    }
    // Hard logout helper (prevents /login <-> / redirect loops)
    const forceLogout = () => {
      clearStoredAuthState();

      // Notify UI layers before redirecting.
      try {
        window.dispatchEvent(new CustomEvent('damyar:auth-expired'));
      } catch (eventError) {
        // ignore event dispatch failures
      }

      // Avoid redirect loops
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };

    const isAuthEndpoint = isAuthEndpointUrl(originalRequest.url);

    // ONLY 401 means "token expired / invalid" → try refresh.
    // 403 means "authenticated but forbidden" → do NOT refresh/logout.
    const shouldAttemptRefresh = error.response?.status === 401;

    const excelImport = originalRequest?._damyarExcelImport;
    const willRetryAfterRefresh = shouldAttemptRefresh && !originalRequest._retry && !isAuthEndpoint;
    if (excelImport && !willRetryAfterRefresh) {
      dispatchExcelImportEvent({
        id: excelImport.id,
        url: excelImport.url,
        file_name: excelImport.fileName,
        phase: 'failed',
        status: 'failed',
        upload_percent: 100,
        progress_percent: 100,
        job_id: error?.response?.headers?.['x-import-job-id'] || error?.response?.headers?.['X-Import-Job-ID'],
        import_batch: error?.response?.headers?.['x-import-batch'] || error?.response?.headers?.['X-Import-Batch'],
        message: getErrorMessage(error, 'ایمپورت اکسل با خطا متوقف شد.'),
      });
    }

    const maintenancePayload = error.response?.status === 423
      ? (error.response?.data?.system_update || {
          active: true,
          blocking_writes: true,
          message: getErrorMessage(error, 'دیتابیس در حال بروزرسانی است. لطفاً صبر کنید.'),
        })
      : null;

    if (maintenancePayload) {
      try {
        window.dispatchEvent(new CustomEvent('damyar:system-update-status', {
          detail: maintenancePayload,
        }));
      } catch (eventError) {
        // ignore UI notification failures
      }
    }

    const shouldNotifyUi = !originalRequest?._damyarSuppressGlobalErrorToast && !isAuthEndpoint && (error.response?.status >= 500 || !error.response || error.response?.status === 423);
    if (shouldNotifyUi) {
      try {
        const requestId = getResponseRequestId(error);
        const rolledBack = wasWriteRolledBack(error);
        const atomicMode = getWriteAtomicMode(error);
        const unsafeRequest = isUnsafeMethod(originalRequest?.method);
        const bypassedAtomicWrite = unsafeRequest && atomicMode === 'BYPASS' && !rolledBack;
        const baseMessage = getErrorMessage(
          error,
          maintenancePayload ? 'دیتابیس در حال بروزرسانی است. لطفاً صبر کنید.' : 'خطای ارتباط با سرور رخ داد.'
        );
        const messageWithTracking = requestId && !baseMessage.includes(requestId)
          ? `${baseMessage} (شناسه پیگیری: ${requestId})`
          : baseMessage;
        const finalMessage = rolledBack
          ? `${messageWithTracking} اطلاعات این درخواست ذخیره نشد.`
          : bypassedAtomicWrite
            ? `${messageWithTracking} این عملیات طولانی خارج از تراکنش سراسری اجرا شده است؛ ممکن است بخشی از اطلاعات ثبت شده باشد. سوابق ایمپورت یا بکاپ را بررسی کنید.`
            : uncertainWriteResult
              ? `${messageWithTracking} وضعیت نهایی ثبت هنوز قطعی نیست؛ سامانه شناسه یکتای درخواست را نگه داشته و تلاش مجدد همان اطلاعات نباید رکورد تکراری بسازد.`
              : messageWithTracking;
        const eventName = postWriteRefreshContext ? 'damyar:write-response-warning' : 'damyar:request-error';
        window.dispatchEvent(new CustomEvent(eventName, {
          detail: {
            message: finalMessage,
            status: error.response?.status || null,
            requestId,
            rolledBack,
            atomicMode,
            bypassedAtomicWrite,
            writeSaved: Boolean(postWriteRefreshContext),
            refreshFailed: Boolean(postWriteRefreshContext),
            errorKind,
            durationMs: finalDiagnostic?.duration_ms ?? null,
            serverDurationMs: finalDiagnostic?.server_duration_ms ?? null,
            timeoutMs: finalDiagnostic?.timeout_ms ?? null,
            retryCount: finalDiagnostic?.retry_count ?? 0,
            clientRequestId: finalDiagnostic?.client_request_id ?? null,
            idempotencyKey: originalRequest?._damyarIdempotencyKey || null,
            uncertainWriteResult,
          },
        }));
      } catch (eventError) {
        // ignore UI notification failures
      }
    }

    if (shouldAttemptRefresh && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const access = await refreshAccessToken();
          if (access) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${access}`;
            setAuthHeader(access);
            return api(originalRequest);
          }
          forceLogout();
        } catch (refreshError) {
          forceLogout();
          return Promise.reject(refreshError);
        }
      } else {
        forceLogout();
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password, loginContext = {}) => 
    api.post('/auth/login/', { username, password, login_context: loginContext }),
  
  getProfile: () => 
    api.get('/auth/profile/'),
  
  updateProfile: (data) => 
    api.patch('/auth/profile/', data),
  
  changePassword: (data) => 
    api.put('/auth/change-password/', data),

  logout: () =>
    api.post('/auth/logout/', {}),
  
  getUsers: (params) => 
    api.get('/auth/users/', { params }),
  
  createUser: (data) => 
    api.post('/auth/users/', data),
  
  updateUser: (id, data) => 
    api.patch(`/auth/users/${id}/`, data),
  
  deleteUser: (id) => 
    api.delete(`/auth/users/${id}/`),

  getRoles: (params) =>
    api.get('/auth/roles/', { params }),

  createRole: (data) =>
    api.post('/auth/roles/', data),

  updateRole: (id, data) =>
    api.patch(`/auth/roles/${id}/`, data),

  deleteRole: (id) =>
    api.delete(`/auth/roles/${id}/`),

  getPermissionCatalog: () =>
    api.get('/auth/permission-catalog/'),

  getUserOptions: (params) =>
    api.get('/auth/user-options/', { params }),
};

// Farm API (Baharband, Cow, Drug)
export const farmAPI = {
  // Baharbands
  getBaharbands: async (params) => {
    try {
      return await cachedGet('/baharbands/', { params, _damyarSuppressGlobalErrorToast: true, cache: { ttl: CACHE_TTL.LONG, key: `baharbands:list:${stableStringify(params || {})}` } });
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 400 && status !== 404 && status !== 500) throw error;
      return cachedGet('/baharbands/options/', { params, _damyarSuppressGlobalErrorToast: true, cache: { ttl: CACHE_TTL.LONG, key: `baharbands:options:${stableStringify(params || {})}` } });
    }
  },
  
  getBaharband: (id) => 
    cachedGet(`/baharbands/${id}/`, { cache: { ttl: CACHE_TTL.LONG } }),
  
  createBaharband: (data) => 
    api.post('/baharbands/', data),
  
  updateBaharband: (id, data) => 
    api.patch(`/baharbands/${id}/`, data),
  
  deleteBaharband: (id) => 
    api.delete(`/baharbands/${id}/`),


  getBaharbandAnimals: (id, params) =>
    api.get(`/baharbands/${id}/animals/`, { params }),

  getBaharbandDailyMovementReport: (params) =>
    api.get('/baharbands/daily-movement-report/', { params, timeout: 900000 }),

  // Operational people / farm staff
  getOperationalPeople: (params) => cachedGet('/operational-people/', { params, cache: { ttl: CACHE_TTL.SHORT, key: `operational-people:${stableStringify(params || {})}` } }),
  getOperationalPersonOptions: (params) => cachedGet('/operational-people/options/', { params, cache: { ttl: CACHE_TTL.MEDIUM, key: `operational-people:options:${stableStringify(params || {})}` } }),
  createOperationalPerson: (data) => api.post('/operational-people/', data),
  updateOperationalPerson: (id, data) => api.patch(`/operational-people/${id}/`, data),
  deleteOperationalPerson: (id) => api.delete(`/operational-people/${id}/`),

  // Site / Unit context
  getHerdSites: (params) => cachedGet('/herd-sites/', { params, _damyarSuppressGlobalErrorToast: true, cache: { ttl: CACHE_TTL.MEDIUM, key: `herd-sites:${stableStringify(params || {})}` } }),
  getHerdUnits: (params) => cachedGet('/herd-units/', { params, _damyarSuppressGlobalErrorToast: true, cache: { ttl: CACHE_TTL.MEDIUM, key: `herd-units:${stableStringify(params || {})}` } }),

  importBaharbandsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only !== undefined) formData.append('validate_only', options.validate_only ? '1' : '0');
    if (options.create_if_missing !== undefined) formData.append('create_if_missing', options.create_if_missing ? '1' : '0');
    if (Array.isArray(options.update_fields)) {
      options.update_fields.filter(Boolean).forEach((field) => formData.append('update_fields', field));
    }
    return api.post('/baharbands/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },



  // Historical daily herd snapshots
  getDailyHerdSnapshots: (params) => api.get('/herd-daily-snapshots/', { params, timeout: 900000 }),
  getDailyHerdSnapshotDetail: (snapshotDate) => api.get(`/herd-daily-snapshots/${snapshotDate}/`, { timeout: 900000 }),
  captureDailyHerdSnapshot: (snapshotDate) => api.post('/herd-daily-snapshots/capture/', { snapshot_date: snapshotDate }, { timeout: 900000 }),
  exportDailyHerdSnapshots: (params) => api.get('/herd-daily-snapshots/export/', { params, responseType: 'blob', timeout: 900000 }),
  getDailyHerdSnapshotAnimals: (params) => api.get('/herd-daily-snapshots/animals/', { params, timeout: 900000 }),
  getDailyHerdSnapshotAnalytics: (params) => api.get('/herd-daily-snapshots/analytics/', { params, timeout: 900000 }),
  exportDailyHerdSnapshotAnalytics: (params) => api.get('/herd-daily-snapshots/analytics/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob', timeout: 900000 }),
  exportDailyHerdSnapshotAnalyticsPDF: (params) => api.get('/herd-daily-snapshots/analytics/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob', timeout: 900000 }),
  backfillDailyHerdSnapshots: (data) => api.post('/herd-daily-snapshots/backfill/', data, { timeout: 900000 }),

  // Cows
  getCows: (params) => cachedGet('/cows/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportActiveCowsBasic: (params) => api.get('/cows/export-active-basic/', { params, responseType: 'blob', timeout: 900000 }),
  
  getCow: (id) => 
    cachedGet(`/cows/${id}/`, { cache: { ttl: CACHE_TTL.MEDIUM } }),

  getDuplicateCowEarTags: (params) =>
    api.get('/cows/duplicate-ear-tags/', { params }),

  mergeDuplicateCows: (data) =>
    api.post('/cows/merge-duplicates/', data),

  batchMergeDuplicateCows: (data) =>
    api.post('/cows/merge-duplicates/batch/', data),

  // Cow vet card (full timeline)
  getCowVetCard: (id, params) => cachedGet(`/cows/${id}/vet-card/`, { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  getCowMilkHealthImpact: (id, params) => api.get(`/cows/${id}/milk-health-impact/`, { params }),
  getCowFertilityHealthImpact: (id, params) => api.get(`/repro/cows/${id}/fertility-health-impact/`, { params }),
  getCowMovementHistory: (id, params) => api.get(`/cows/${id}/movement-history/`, { params }),
  exportCowMovementHistoryExcel: (id, params) => api.get(`/cows/${id}/movement-history/`, { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportCowMovementHistoryPDF: (id, params) => api.get(`/cows/${id}/movement-history/`, { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  exportCowVetCardExcel: (id, params) => api.get(`/cows/${id}/vet-card/excel/`, { params, responseType: 'blob' }),
  exportCowVetCardPDF: (id, params) => api.get(`/cows/${id}/vet-card/pdf/`, { params, responseType: 'blob' }),
  getCowPhase2Summary: (id) => cachedGet(`/cows/${id}/phase2-summary/`, { cache: { ttl: CACHE_TTL.MEDIUM } }),
  getCowStatusInsight: (id, params) => api.get(`/cows/${id}/status-insight/`, { params }),
  getCowStatusExplanation: (id, params) => api.get(`/cows/${id}/status-explanation/`, { params }),
  getCowTimelineCanonical: (id, params) => cachedGet(`/cows/${id}/timeline/`, { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getCowManagementFlags: (id, params) => cachedGet(`/cows/${id}/management-flags/`, { params, cache: { ttl: CACHE_TTL.SHORT } }),
  setCowManagementFlag: (id, data) => api.post(`/cows/${id}/management-flags/set/`, data),
  clearCowManagementFlag: (id, data) => api.post(`/cows/${id}/management-flags/clear/`, data),
  rebuildCowStatuses: (data) => api.post('/cows/status-rebuild/', data),
  getCowLactationCurve: (id) => cachedGet(`/cows/${id}/lactation-curve/`, { cache: { ttl: CACHE_TTL.MEDIUM } }),
  getCowDIM: (id) => cachedGet(`/cows/${id}/dim/`, { cache: { ttl: CACHE_TTL.MEDIUM } }),
  
  createCow: (data) => 
    api.post('/cows/', data),
  
  updateCow: (id, data) => 
    api.patch(`/cows/${id}/`, data),
  
  deleteCow: (id) => 
    api.delete(`/cows/${id}/`),

  // Soft remove cow (sets removed_at + removed_reason, and deactivates)
  removeCow: (id, data) => api.post(`/cows/${id}/remove/`, data),

  // Hospital workflow
  getHospitalAdmissions: (params) => api.get('/hospital-admissions/', { params }),
  getHospitalAdmission: (id) => api.get(`/hospital-admissions/${id}/`),
  createHospitalAdmission: (data) => api.post('/hospital-admissions/', data),
  updateHospitalAdmission: (id, data) => api.patch(`/hospital-admissions/${id}/`, data),
  dischargeHospitalAdmission: (id, data) => api.post(`/hospital-admissions/${id}/discharge/`, data),
  getHospitalAdmissionsSummary: (params) => api.get('/hospital-admissions/summary/', { params }),
  exportHospitalAdmissionsExcel: (params) => api.get('/hospital-admissions/export/', { params, responseType: 'blob', timeout: 900000 }),
  getHospitalDailyChecks: (params) => api.get('/hospital-daily-checks/', { params }),
  createHospitalDailyCheck: (data) => api.post('/hospital-daily-checks/', data),
  updateHospitalDailyCheck: (id, data) => api.patch(`/hospital-daily-checks/${id}/`, data),




  // Fresh cow 30-day monitoring
  getFreshCowChecks: (params) => api.get('/fresh-cow-checks/', { params }),
  getFreshCowCheckSummary: (params) => api.get('/fresh-cow-checks/summary/', { params, timeout: 900000 }),
  createFreshCowCheck: (data) => api.post('/fresh-cow-checks/', data),
  updateFreshCowCheck: (id, data) => api.patch(`/fresh-cow-checks/${id}/`, data),
  exportFreshCowChecksExcel: (params) => api.get('/fresh-cow-checks/export/', { params, responseType: 'blob', timeout: 900000 }),

  // Veterinary laboratory
  getLabTestTypes: (params) => api.get('/lab-test-types/', { params }),
  createLabTestType: (data) => api.post('/lab-test-types/', data),
  updateLabTestType: (id, data) => api.patch(`/lab-test-types/${id}/`, data),
  deleteLabTestType: (id) => api.delete(`/lab-test-types/${id}/`),
  getLabSamples: (params) => api.get('/lab-samples/', { params }),
  getLabSamplesSummary: (params) => api.get('/lab-samples/summary/', { params }),
  createLabSample: (data) => api.post('/lab-samples/', data),
  updateLabSample: (id, data) => api.patch(`/lab-samples/${id}/`, data),
  exportLabSamplesExcel: (params) => api.get('/lab-samples/export/', { params, responseType: 'blob', timeout: 900000 }),
  getLabResults: (params) => api.get('/lab-results/', { params }),
  createLabResult: (data) => api.post('/lab-results/', data),
  updateLabResult: (id, data) => api.patch(`/lab-results/${id}/`, data),
  exportLabResultsExcel: (params) => api.get('/lab-results/export/', { params, responseType: 'blob', timeout: 900000 }),
  getAntibiogramResults: (params) => api.get('/antibiogram-results/', { params }),
  createAntibiogramResult: (data) => api.post('/antibiogram-results/', data),
  updateAntibiogramResult: (id, data) => api.patch(`/antibiogram-results/${id}/`, data),

  // Unified livestock removal / mortality events
  getLivestockExitCauses: (params) => cachedGet('/livestock-exit-causes/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  createLivestockExitCause: (data) => api.post('/livestock-exit-causes/', data),
  updateLivestockExitCause: (id, data) => api.patch(`/livestock-exit-causes/${id}/`, data),
  getLivestockExitEvents: (params) => api.get('/livestock-exit-events/', { params }),
  createLivestockExitEvent: (data) => api.post('/livestock-exit-events/', data),
  updateLivestockExitEvent: (id, data) => api.patch(`/livestock-exit-events/${id}/`, data),
  getLivestockNecropsyPhotos: (params) => api.get('/livestock-necropsy-photos/', { params }),
  createLivestockNecropsyPhoto: (data) => api.post('/livestock-necropsy-photos/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLivestockNecropsyPhoto: (id) => api.delete(`/livestock-necropsy-photos/${id}/`),
  getLivestockNecropsyPhotoImageUrl: (id) => `${API_URL}/livestock-necropsy-photos/${id}/image/`,
  getMedicationWithdrawalReport: (params) => api.get('/medication-batches/withdrawal-report/', { params }),
  exportMedicationWithdrawalReportExcel: (params) => api.get('/medication-batches/withdrawal-report/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportHealthHistoryExcel: (params) => api.get('/reports/health-history-export/', { params: withXlsxExportParams(params), responseType: 'blob' }),
  exportHealthHistoryPDF: (params) => api.get('/reports/health-history-export/', { params: withPdfExportParams(params), responseType: 'blob' }),
  createPreventiveCareEventsBulk: (data) => api.post('/preventive-care-events/bulk-create/', data),
  
  getCowsSelectList: async (params) => {
    const normalizedParams = (() => {
      const p = { ...(params || {}) };
      if (p.search !== undefined) p.search = normalizeDigits(p.search);
      if (p.q !== undefined) p.q = normalizeDigits(p.q);
      if (p.baharband !== undefined) p.baharband = normalizeDigits(p.baharband);
      if (p.status !== undefined) p.status = String(p.status).trim();
      if (p.sex !== undefined) p.sex = String(p.sex).trim();
      return p;
    })();

    if (shouldUseDedicatedCowsSelectList()) {
      try {
        const response = await cachedGet('/cows/select_list/', {
          params: normalizedParams,
          cache: { ttl: CACHE_TTL.SHORT },
        });
        markDedicatedCowsSelectListAvailable(true);
        return response;
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        markDedicatedCowsSelectListAvailable(false);
      }
    }

    const fallbackParams = { ...normalizedParams };
    if (fallbackParams.search === undefined && fallbackParams.q !== undefined) {
      fallbackParams.search = fallbackParams.q;
    }
    delete fallbackParams.q;

    const requestedPageSize = Number(fallbackParams.page_size);
    fallbackParams.page_size = Number.isFinite(requestedPageSize)
      ? Math.max(1, Math.min(requestedPageSize, 200))
      : 50;

    const response = await cachedGet('/cows/', {
      params: fallbackParams,
      cache: { ttl: CACHE_TTL.SHORT },
    });

    const normalizedRows = extractListData(response).map((item) => normalizeCowSelectOption(item));
    return {
      ...response,
      data: normalizedRows,
      fromSelectListFallback: true,
    };
  },

  bulkMoveCows: (cow_ids, target_baharband, extra = {}) =>
    api.post('/cows/bulk_move/', { cow_ids, target_baharband, ...extra }),

  moveCow: (id, target_baharband, extra = {}) =>
    api.post(`/cows/${id}/move/`, { target_baharband, ...extra }),

  registerCowBCS: (id, data) =>
    api.post(`/cows/${id}/register-bcs/`, data),

  bulkRegisterCowBCS: (entries, extra = {}) =>
    api.post('/cows/bulk-register-bcs/', { entries, ...extra }),

  swapBaharbands: (source_baharband, target_baharband, extra = {}) =>
    api.post('/baharbands/swap/', { source_baharband, target_baharband, ...extra }),

  importCowsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only !== undefined) formData.append('validate_only', options.validate_only ? '1' : '0');
    if (options.create_if_missing !== undefined) formData.append('create_if_missing', options.create_if_missing ? '1' : '0');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (Array.isArray(options.update_fields)) {
      options.update_fields.filter(Boolean).forEach((field) => formData.append('update_fields', field));
    }
    return api.post('/cows/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadExcelTemplate: (templateName) =>
    api.get(`/templates/${templateName}/`, { responseType: 'blob' }),

  // Drugs
  getDrugs: (params) => 
    api.get('/drugs/', { params }),
  
  getDrug: (id) => 
    api.get(`/drugs/${id}/`),
  
  createDrug: (data) => 
    api.post('/drugs/', data),
  
  updateDrug: (id, data) => 
    api.patch(`/drugs/${id}/`, data),
  
  deleteDrug: (id) => 
    api.delete(`/drugs/${id}/`),

  adjustDrugStock: (id, data) => api.post(`/drugs/${id}/adjust-stock/`, data),
  getDrugLots: (params) => api.get('/drug-lots/', { params }),
  createDrugLot: (data) => api.post('/drug-lots/', data),
  updateDrugLot: (id, data) => api.patch(`/drug-lots/${id}/`, data),
  adjustDrugLot: (id, data) => api.post(`/drug-lots/${id}/adjust/`, data),
  getDrugInventoryMovements: (params) => api.get('/drug-inventory-movements/', { params }),
  getDrugMonthlyPrices: (params) => api.get('/drug-monthly-prices/', { params }),
  createDrugMonthlyPrice: (data) => api.post('/drug-monthly-prices/', data),
  updateDrugMonthlyPrice: (id, data) => api.patch(`/drug-monthly-prices/${id}/`, data),
  deleteDrugMonthlyPrice: (id) => api.delete(`/drug-monthly-prices/${id}/`),


  // Feed & procurement (Phase 3)
  getFeedCategories: (params) => api.get('/feed-categories/', { params }),
  createFeedCategory: (data) => api.post('/feed-categories/', data),
  updateFeedCategory: (id, data) => api.patch(`/feed-categories/${id}/`, data),
  deleteFeedCategory: (id) => api.delete(`/feed-categories/${id}/`),

  getFeedWasteCauses: (params) => api.get('/feed-waste-causes/', { params }),
  createFeedWasteCause: (data) => api.post('/feed-waste-causes/', data),
  updateFeedWasteCause: (id, data) => api.patch(`/feed-waste-causes/${id}/`, data),
  deleteFeedWasteCause: (id) => api.delete(`/feed-waste-causes/${id}/`),

  getFeedLossCauses: (params) => api.get('/feed-loss-causes/', { params }),
  createFeedLossCause: (data) => api.post('/feed-loss-causes/', data),
  updateFeedLossCause: (id, data) => api.patch(`/feed-loss-causes/${id}/`, data),
  deleteFeedLossCause: (id) => api.delete(`/feed-loss-causes/${id}/`),

  getFeedResidualCauses: (params) => api.get('/feed-residual-causes/', { params }),
  createFeedResidualCause: (data) => api.post('/feed-residual-causes/', data),
  updateFeedResidualCause: (id, data) => api.patch(`/feed-residual-causes/${id}/`, data),
  deleteFeedResidualCause: (id) => api.delete(`/feed-residual-causes/${id}/`),

  getFeedOverheadTypes: (params) => api.get('/feed-overhead-types/', { params }),
  createFeedOverheadType: (data) => api.post('/feed-overhead-types/', data),
  updateFeedOverheadType: (id, data) => api.patch(`/feed-overhead-types/${id}/`, data),
  deleteFeedOverheadType: (id) => api.delete(`/feed-overhead-types/${id}/`),

  getFeedItems: (params) => api.get('/feed-items/', { params }),
  createFeedItem: (data) => api.post('/feed-items/', data),
  updateFeedItem: (id, data) => api.patch(`/feed-items/${id}/`, data),
  deleteFeedItem: (id) => api.delete(`/feed-items/${id}/`),
  adjustFeedStock: (id, data) => api.post(`/feed-items/${id}/adjust_stock/`, data),
  getFeedInventoryMovements: (params) => api.get('/feed-inventory-movements/', { params }),

  getFeedingGroups: (params) => api.get('/feeding-groups/', { params }),
  createFeedingGroup: (data) => api.post('/feeding-groups/', data),
  updateFeedingGroup: (id, data) => api.patch(`/feeding-groups/${id}/`, data),
  deleteFeedingGroup: (id) => api.delete(`/feeding-groups/${id}/`),
  syncFeedingGroupHeadCount: (id) => api.post(`/feeding-groups/${id}/sync-head-count/`),

  getRations: (params) => api.get('/rations/', { params }),
  getRation: (id) => api.get(`/rations/${id}/`),
  createRation: (data) => api.post('/rations/', data),
  updateRation: (id, data) => api.patch(`/rations/${id}/`, data),
  deleteRation: (id) => api.delete(`/rations/${id}/`),
  cloneRation: (id, data = {}) => api.post(`/rations/${id}/clone/`, data),
  activateRation: (id, data = {}) => api.post(`/rations/${id}/activate/`, data),
  deactivateRation: (id) => api.post(`/rations/${id}/deactivate/`),
  getRationCostSummary: (id) => api.get(`/rations/${id}/cost-summary/`),
  getRationItemsByRation: (id) => api.get(`/rations/${id}/items/`),

  getRationItems: (params) => api.get('/ration-items/', { params }),
  createRationItem: (data) => api.post('/ration-items/', data),
  updateRationItem: (id, data) => api.patch(`/ration-items/${id}/`, data),
  deleteRationItem: (id) => api.delete(`/ration-items/${id}/`),

  getFeedPurchases: (params) => api.get('/feed-purchases/', { params }),
  createFeedPurchase: (data) => api.post('/feed-purchases/', data),

  getFeedPlannedConsumptions: (params) => api.get('/feed-planned-consumptions/', { params }),
  getFeedPlannedConsumption: (id) => api.get(`/feed-planned-consumptions/${id}/`),
  createFeedPlannedConsumption: (data) => api.post('/feed-planned-consumptions/', data),
  updateFeedPlannedConsumption: (id, data) => api.patch(`/feed-planned-consumptions/${id}/`, data),
  deleteFeedPlannedConsumption: (id) => api.delete(`/feed-planned-consumptions/${id}/`),
  finalizeFeedPlannedConsumption: (id) => api.post(`/feed-planned-consumptions/${id}/finalize/`),
  cancelFeedPlannedConsumption: (id) => api.post(`/feed-planned-consumptions/${id}/cancel/`),
  getFeedPlannedConsumptionItems: (id) => api.get(`/feed-planned-consumptions/${id}/items/`),

  getFeedScaleTickets: (params) => api.get('/feed-scale-tickets/', { params }),
  getFeedScaleTicket: (id) => api.get(`/feed-scale-tickets/${id}/`),
  createFeedScaleTicket: (data, config = {}) => api.post('/feed-scale-tickets/', data, config),
  updateFeedScaleTicket: (id, data, config = {}) => api.patch(`/feed-scale-tickets/${id}/`, data, config),
  deleteFeedScaleTicket: (id) => api.delete(`/feed-scale-tickets/${id}/`),
  finalizeFeedScaleTicket: (id) => api.post(`/feed-scale-tickets/${id}/finalize/`),
  cancelFeedScaleTicket: (id) => api.post(`/feed-scale-tickets/${id}/cancel/`),
  getFeedScaleTicketCompare: (id) => api.get(`/feed-scale-tickets/${id}/compare-with-plan/`),
  getFeedScaleTicketGeneratedItems: (id) => api.get(`/feed-scale-tickets/${id}/generated-items/`),
  getFeedScaleTicketDistribution: (id) => api.get(`/feed-scale-tickets/${id}/distribution/`),
  distributeFeedScaleTicket: (id, data) => api.post(`/feed-scale-tickets/${id}/distribution/`, data),
  deleteFeedScaleTicketDistribution: (id) => api.delete(`/feed-scale-tickets/${id}/distribution/`),

  getFeedScaleDistributions: (params) => api.get('/feed-scale-distributions/', { params }),
  getFeedScaleDistribution: (id) => api.get(`/feed-scale-distributions/${id}/`),
  createFeedScaleDistribution: (data) => api.post('/feed-scale-distributions/', data),
  updateFeedScaleDistribution: (id, data) => api.patch(`/feed-scale-distributions/${id}/`, data),
  deleteFeedScaleDistribution: (id) => api.delete(`/feed-scale-distributions/${id}/`),

  getFeedConsumptions: (params) => api.get('/feed-consumptions/', { params }),
  getFeedConsumption: (id) => api.get(`/feed-consumptions/${id}/`),
  createFeedConsumption: (data) => api.post('/feed-consumptions/', data),
  updateFeedConsumption: (id, data) => api.patch(`/feed-consumptions/${id}/`, data),
  deleteFeedConsumption: (id) => api.delete(`/feed-consumptions/${id}/`),

  getFeedWastes: (params) => api.get('/feed-wastes/', { params }),
  getFeedWaste: (id) => api.get(`/feed-wastes/${id}/`),
  createFeedWaste: (data, config = {}) => api.post('/feed-wastes/', data, config),
  updateFeedWaste: (id, data, config = {}) => api.patch(`/feed-wastes/${id}/`, data, config),
  deleteFeedWaste: (id) => api.delete(`/feed-wastes/${id}/`),

  getFeedLosses: (params) => api.get('/feed-losses/', { params }),
  getFeedLoss: (id) => api.get(`/feed-losses/${id}/`),
  createFeedLoss: (data, config = {}) => api.post('/feed-losses/', data, config),
  updateFeedLoss: (id, data, config = {}) => api.patch(`/feed-losses/${id}/`, data, config),
  deleteFeedLoss: (id) => api.delete(`/feed-losses/${id}/`),

  getFeedResiduals: (params) => api.get('/feed-residuals/', { params }),
  getFeedResidual: (id) => api.get(`/feed-residuals/${id}/`),
  createFeedResidual: (data, config = {}) => api.post('/feed-residuals/', data, config),
  updateFeedResidual: (id, data, config = {}) => api.patch(`/feed-residuals/${id}/`, data, config),
  deleteFeedResidual: (id) => api.delete(`/feed-residuals/${id}/`),

  getFeedOverheads: (params) => api.get('/feed-overheads/', { params }),
  getFeedOverhead: (id) => api.get(`/feed-overheads/${id}/`),
  createFeedOverhead: (data) => api.post('/feed-overheads/', data),
  updateFeedOverhead: (id, data) => api.patch(`/feed-overheads/${id}/`, data),
  deleteFeedOverhead: (id) => api.delete(`/feed-overheads/${id}/`),
  getFeedOverheadAllocations: (id) => api.get(`/feed-overheads/${id}/allocations/`),
  allocateFeedOverhead: (id, data) => api.post(`/feed-overheads/${id}/allocate/`, data),

  getSuppliers: (params) => api.get('/suppliers/', { params }),
  createSupplier: (data) => api.post('/suppliers/', data),
  updateSupplier: (id, data) => api.patch(`/suppliers/${id}/`, data),
  deleteSupplier: (id) => api.delete(`/suppliers/${id}/`),

  getSupplyItems: (params) => api.get('/supply-items/', { params }),
  createSupplyItem: (data) => api.post('/supply-items/', data),
  updateSupplyItem: (id, data) => api.patch(`/supply-items/${id}/`, data),
  deleteSupplyItem: (id) => api.delete(`/supply-items/${id}/`),
  adjustSupplyStock: (id, data) => api.post(`/supply-items/${id}/adjust_stock/`, data),
  getSupplyInventoryMovements: (params) => api.get('/supply-inventory-movements/', { params }),

  getSupplyPurchases: (params) => api.get('/supply-purchases/', { params }),
  createSupplyPurchase: (data) => api.post('/supply-purchases/', data),

  getSupplyConsumptions: (params) => api.get('/supply-consumptions/', { params }),
  createSupplyConsumption: (data) => api.post('/supply-consumptions/', data),

  getPurchaseRequests: (params) => api.get('/purchase-requests/', { params }),
  createPurchaseRequest: (data) => api.post('/purchase-requests/', data),
  updatePurchaseRequest: (id, data) => api.patch(`/purchase-requests/${id}/`, data),
  setPurchaseRequestStatus: (id, status) => api.post(`/purchase-requests/${id}/set_status/`, { status }),

  getPurchaseOrders: (params) => api.get('/purchase-orders/', { params }),
  createPurchaseOrder: (data) => api.post('/purchase-orders/', data),
  updatePurchaseOrder: (id, data) => api.patch(`/purchase-orders/${id}/`, data),
  receivePurchaseOrder: (id, data) => api.post(`/purchase-orders/${id}/receive/`, data),

  getStorageLocations: (params) => api.get('/storage-locations/', { params }),
  createStorageLocation: (data) => api.post('/storage-locations/', data),
  updateStorageLocation: (id, data) => api.patch(`/storage-locations/${id}/`, data),
  deleteStorageLocation: (id) => api.delete(`/storage-locations/${id}/`),
  importStorageLocationsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.source) formData.append('source', options.source);
    return api.post('/storage-locations/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  getSemenStoragePositions: (params) => api.get('/semen-storage-positions/', { params }),
  createSemenStoragePosition: (data) => api.post('/semen-storage-positions/', data),
  updateSemenStoragePosition: (id, data) => api.patch(`/semen-storage-positions/${id}/`, data),
  deleteSemenStoragePosition: (id) => api.delete(`/semen-storage-positions/${id}/`),

  getGoodsReceipts: (params) => api.get('/goods-receipts/', { params }),
  createGoodsReceipt: (data) => api.post('/goods-receipts/', data),
  updateGoodsReceipt: (id, data) => api.patch(`/goods-receipts/${id}/`, data),
  postGoodsReceipt: (id) => api.post(`/goods-receipts/${id}/post_receipt/`),
  reverseGoodsReceipt: (id) => api.post(`/goods-receipts/${id}/reverse_receipt/`),
  importGoodsReceiptsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.source) formData.append('source', options.source);
    return api.post('/goods-receipts/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  getOpeningInventories: (params) => api.get('/opening-inventories/', { params }),
  createOpeningInventory: (data) => api.post('/opening-inventories/', data),
  updateOpeningInventory: (id, data) => api.patch(`/opening-inventories/${id}/`, data),
  postOpeningInventory: (id) => api.post(`/opening-inventories/${id}/post_opening/`),
  importOpeningInventoriesExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.source) formData.append('source', options.source);
    return api.post('/opening-inventories/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  getStockCounts: (params) => api.get('/stock-counts/', { params }),
  createStockCount: (data) => api.post('/stock-counts/', data),
  updateStockCount: (id, data) => api.patch(`/stock-counts/${id}/`, data),
  postStockCount: (id) => api.post(`/stock-counts/${id}/post_count/`),
  importStockCountsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.source) formData.append('source', options.source);
    return api.post('/stock-counts/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  getInventoryOnHand: (params) => api.get('/inventory/on-hand/', { params }),
  getInventoryItemCard: (params) => api.get('/inventory/item-card/', { params }),

  // Diseases
  getDiseases: (params) => api.get('/diseases/', { params }),
  createDisease: (data) => api.post('/diseases/', data),
  updateDisease: (id, data) => api.patch(`/diseases/${id}/`, data),
  deleteDisease: (id) => api.delete(`/diseases/${id}/`),
  getDiseaseCategories: (params) => api.get('/diseases/categories/', { params }),
  getDiseaseChildren: (params) => api.get('/diseases/children/', { params }),
  getDiseaseTree: (params) => api.get('/diseases/tree/', { params }),

  getDiseaseAffectedParts: (params) => api.get('/disease-affected-parts/', { params }),
  createDiseaseAffectedPart: (data) => api.post('/disease-affected-parts/', data),
  updateDiseaseAffectedPart: (id, data) => api.patch(`/disease-affected-parts/${id}/`, data),
  deleteDiseaseAffectedPart: (id) => api.delete(`/disease-affected-parts/${id}/`),

  // Cow individual: measurements (height/weight)
  getCowMeasurements: (params) =>
    api.get('/cow-measurements/', { params }),

  createCowMeasurement: (data) =>
    api.post('/cow-measurements/', data),
  updateCowMeasurement: (id, data) =>
    api.patch(`/cow-measurements/${id}/`, data),
  deleteCowMeasurement: (id) =>
    api.delete(`/cow-measurements/${id}/`),

  // Cow individual: appearance (breed/color)
  getCowAppearances: (params) =>
    api.get('/cow-appearances/', { params }),

  createCowAppearance: (data) =>
    api.post('/cow-appearances/', data),

  updateCowAppearance: (id, data) =>
    api.patch(`/cow-appearances/${id}/`, data),

  // Cow individual: photos (URL)
  getCowPhotos: (params) =>
    api.get('/cow-photos/', { params }),

  createCowPhoto: (data) =>
    api.post('/cow-photos/', data),

  // Calf two-sided photo sketch capture
  getCalfPhotoSketches: (params) =>
    api.get('/calf-photo-sketches/', { params }),

  getCalfPhotoSketchImageUrl: (id, field = 'sketch_image') => {
    const allowed = new Set(['left_photo', 'right_photo', 'sketch_image']);
    const safeField = allowed.has(field) ? field : 'sketch_image';
    const base = String(api.defaults.baseURL || '/api').replace(/\/$/, '');
    return `${base}/calf-photo-sketches/${encodeURIComponent(id)}/image/?field=${encodeURIComponent(safeField)}`;
  },

  downloadCalfPhotoSketchPdf: (id) =>
    api.get(`/calf-photo-sketches/${encodeURIComponent(id)}/pdf/`, { responseType: 'blob' }),

  createCalfPhotoSketch: (formData, onUploadProgress) =>
    api.post('/calf-photo-sketches/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),

  updateCalfPhotoSketch: (id, formData, onUploadProgress) =>
    api.patch(`/calf-photo-sketches/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),

  deleteCalfPhotoSketch: (id) =>
    api.delete(`/calf-photo-sketches/${id}/`),

  // Async visual search for an unknown calf photo. The POST only queues the
  // job; polling the job endpoint keeps long-running comparisons out of the
  // HTTP request path and prevents proxy/gunicorn timeouts.
  createCalfImageSearchJob: (formData, onUploadProgress) =>
    api.post('/calf-image-search-jobs/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 120000,
    }),

  getCalfImageSearchJob: (id) =>
    api.get(`/calf-image-search-jobs/${encodeURIComponent(id)}/`, { timeout: 30000 }),

  getCalfImageSearchImageUrl: (id) => {
    const base = String(api.defaults.baseURL || '/api').replace(/\/$/, '');
    return `${base}/calf-image-search-jobs/${encodeURIComponent(id)}/image/`;
  },

  getCalfImageSearchJobs: (params) =>
    api.get('/calf-image-search-jobs/', { params, timeout: 30000 }),

  setCalfImageSearchDecision: (jobId, resultId, decision) =>
    api.post(`/calf-image-search-jobs/${encodeURIComponent(jobId)}/decision/`, {
      result_id: resultId,
      decision,
    }),

  getCalfRecognitionLearningStatus: () =>
    api.get('/calf-image-search-jobs/learning-status/', { timeout: 30000 }),

  startCalfRecognitionTraining: () =>
    api.post('/calf-image-search-jobs/train-model/', {}, { timeout: 30000 }),

  // Cow individual: documents
  getCowDocuments: (params) => api.get('/cow-documents/', { params }),
  createCowDocument: (data) => api.post('/cow-documents/', data),
  importCowDocumentsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/cow-documents/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Calf report is used by live calf selectors and must reflect freshly updated
  // birth weights from the animal form / Excel import, so do not cache it.
  getCalvesReport: (params) => api.get('/cows/calves-report/', { params }),
  exportCalvesReportExcel: (params) => api.get('/cows/calves-report/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),

  getCalfGroupWeighings: (params) => api.get('/calf-group-weighings/', { params }),
  getCalfGroupWeighingSummary: (params) => api.get('/calf-group-weighings/summary/', { params }),
  getCalfGroupWeighingAdgAnalysis: (params) => api.get('/calf-group-weighings/adg-analysis/', { params }),
  exportCalfGroupWeighingsPdf: (params) => api.get('/calf-group-weighings/export-pdf/', { params, responseType: 'blob' }),
  downloadCalfGroupWeighingPdf: (id) => api.get(`/calf-group-weighings/${id}/pdf/`, { responseType: 'blob' }),
  createCalfGroupWeighing: (data) => api.post('/calf-group-weighings/', data),
  updateCalfGroupWeighing: (id, data) => api.patch(`/calf-group-weighings/${id}/`, data),
  deleteCalfGroupWeighing: (id) => api.delete(`/calf-group-weighings/${id}/`),

  getCalfAnalyticsDashboard: (params) => cachedGet('/calf-analytics/dashboard/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportCalfAnalyticsDashboardExcel: (params) => api.get('/calf-analytics/dashboard/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),

  getCalfGrowthAnalytics: (params) => cachedGet('/calf-analytics/growth/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportCalfGrowthAnalyticsExcel: (params) => api.get('/calf-analytics/growth/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportCalfGrowthAnalyticsPdf: (params) => api.get('/calf-analytics/growth/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),


  getCalfHealthScores: (params) => api.get('/calf-health-scores/', { params }),
  createCalfHealthScore: (data) => api.post('/calf-health-scores/', data),
  updateCalfHealthScore: (id, data) => api.patch(`/calf-health-scores/${id}/`, data),
  deleteCalfHealthScore: (id) => api.delete(`/calf-health-scores/${id}/`),
  getCalfHealthScoresDashboard: (params) => cachedGet('/calf-health-scores/dashboard/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportCalfHealthScoresDashboardExcel: (params) => api.get('/calf-health-scores/dashboard/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  downloadCalfHealthScoreTemplate: () => api.get('/calf-health-scores/template/', { responseType: 'blob' }),
  importCalfHealthScoresExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/calf-health-scores/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Calf events
  getCalfEvents: (params) => api.get('/calf-events/', { params }),
  createCalfEvent: (data) => api.post('/calf-events/', data),
  bulkCreateCalfEvents: (data) => api.post('/calf-events/bulk-create/', data),
  getCalfMortalityReport: (params) => api.get('/calf-events/mortality-report/', { params }),
  exportCalfMortalityReportExcel: (params) => api.get('/calf-events/mortality-report/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),

  // Calf milk intake records
  getCalfMilkDailyPrices: (params) => api.get('/calf-milk-prices/', { params }),
  createCalfMilkDailyPrice: (data) => api.post('/calf-milk-prices/', data),
  updateCalfMilkDailyPrice: (id, data) => api.patch(`/calf-milk-prices/${id}/`, data),
  deleteCalfMilkDailyPrice: (id) => api.delete(`/calf-milk-prices/${id}/`),
  getCalfMilkRecords: (params) => api.get('/calf-milk-records/', { params }),
  createCalfMilkRecord: (data) => api.post('/calf-milk-records/', data),
  updateCalfMilkRecord: (id, data) => api.patch(`/calf-milk-records/${id}/`, data),
  deleteCalfMilkRecord: (id) => api.delete(`/calf-milk-records/${id}/`),
  getCalfMilkRecordsDashboard: (params) => cachedGet('/calf-milk-records/dashboard/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getCalfMilkMonthlyReport: (params) => cachedGet('/calf-milk-records/monthly-report/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportCalfMilkMonthlyReportExcel: (params) => api.get('/calf-milk-records/monthly-report/', { params: withXlsxExportParams(params), responseType: 'blob' }),
  importCalfEventsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/calf-events/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportCalfEventsExcel: (params) => api.get('/calf-events/export-excel/', {
    params,
    responseType: 'blob',
  }),


  // Cow milk records
  getCowMilkRecords: (params) => api.get('/cow-milk-records/', { params }),
  getCowMilkQualitySummary: (params) => cachedGet('/cow-milk-records/quality-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  getCowMilkProductionAnalytics: (params) => cachedGet('/cow-milk-records/production-analytics/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportCowMilkProductionAnalyticsExcel: (params) => api.get('/cow-milk-records/production-analytics/', { params: { ...params, export: 'xlsx' }, responseType: 'blob' }),
  exportCowMilkProductionAnalyticsPdf: (params) => api.get('/cow-milk-records/production-analytics/', { params: { ...params, export: 'pdf' }, responseType: 'blob' }),
  exportCowMilkQualitySummaryExcel: (params) => api.get('/cow-milk-records/quality-summary/', { params: { ...params, export: 'xlsx' }, responseType: 'blob' }),
  createCowMilkRecord: (data) => api.post('/cow-milk-records/', data),
  importCowMilkRecordsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', options.validate_only ? '1' : '0');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.async_import) formData.append('async_import', '1');
    return api.post('/cow-milk-records/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
    });
  },
  getCowMilkImportProgress: (importBatch) => api.get('/cow-milk-records/import-progress/', { params: { import_batch: importBatch } }),
  downloadCowMilkImportReportPdf: (importBatch) => api.get('/cow-milk-records/import-report-pdf/', {
    params: { import_batch: importBatch },
    responseType: 'blob',
  }),


  // Preventive care
  getPreventiveCareEvents: (params) => api.get('/preventive-care-events/', { params }),
  createPreventiveCareEvent: (data) => api.post('/preventive-care-events/', data),
  updatePreventiveCareEvent: (id, data) => api.patch(`/preventive-care-events/${id}/`, data),
  registerPreventiveCareTiter: (id, data) => api.post(`/preventive-care-events/${id}/register-titer/`, data),
  importPreventiveCareEventsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/preventive-care-events/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportPreventiveCareEventsExcel: (params) => api.get('/preventive-care-events/export-excel/', {
    params,
    responseType: 'blob',
  }),

  // Udder health / mastitis
  getUdderQuarterStatuses: (params) => api.get('/udder-quarter-statuses/', { params }),
  getUdderQuarterStatus: (id) => api.get(`/udder-quarter-statuses/${id}/`),
  getUdderQuarterStatusHistory: (id) => api.get(`/udder-quarter-statuses/${id}/history/`),
  createUdderQuarterStatus: (data) => api.post('/udder-quarter-statuses/', data),
  updateUdderQuarterStatus: (id, data) => api.patch(`/udder-quarter-statuses/${id}/`, data),
  archiveUdderQuarterStatus: (id, data) => api.post(`/udder-quarter-statuses/${id}/archive/`, data || {}),
  restoreUdderQuarterStatus: (id) => api.post(`/udder-quarter-statuses/${id}/restore/`),
  getMastitisCases: (params) => api.get('/mastitis-cases/', { params }),
  getMastitisCase: (id) => api.get(`/mastitis-cases/${id}/`),
  getMastitisCaseHistory: (id) => api.get(`/mastitis-cases/${id}/history/`),
  createMastitisCase: (data) => api.post('/mastitis-cases/', data),
  updateMastitisCase: (id, data) => api.patch(`/mastitis-cases/${id}/`, data),
  runMastitisQuickWorkflow: (id, data) => api.post(`/mastitis-cases/${id}/quick-workflow/`, data || {}),
  archiveMastitisCase: (id, data) => api.post(`/mastitis-cases/${id}/archive/`, data || {}),
  restoreMastitisCase: (id) => api.post(`/mastitis-cases/${id}/restore/`),
  downloadMastitisImportTemplate: () => api.get('/mastitis-cases/template/', { responseType: 'blob' }),
  importMastitisExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.update_existing === false) formData.append('update_existing', '0');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/mastitis-cases/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMastitisFollowups: (params) => api.get('/mastitis-followups/', { params }),
  getMastitisFollowup: (id) => api.get(`/mastitis-followups/${id}/`),
  getMastitisFollowupHistory: (id) => api.get(`/mastitis-followups/${id}/history/`),
  createMastitisFollowup: (data) => api.post('/mastitis-followups/', data),
  updateMastitisFollowup: (id, data) => api.patch(`/mastitis-followups/${id}/`, data),
  archiveMastitisFollowup: (id, data) => api.post(`/mastitis-followups/${id}/archive/`, data || {}),
  restoreMastitisFollowup: (id) => api.post(`/mastitis-followups/${id}/restore/`),

  // Lameness / hoof-trimming
  getLamenessEvents: (params) => api.get('/lameness-events/', { params }),
  getLamenessEvent: (id) => api.get(`/lameness-events/${id}/`),
  getLamenessEventHistory: (id) => api.get(`/lameness-events/${id}/history/`),
  createLamenessEvent: (data) => api.post('/lameness-events/', data),
  updateLamenessEvent: (id, data) => api.patch(`/lameness-events/${id}/`, data),
  runLamenessQuickWorkflow: (id, data) => api.post(`/lameness-events/${id}/quick-workflow/`, data || {}),
  archiveLamenessEvent: (id, data) => api.post(`/lameness-events/${id}/archive/`, data || {}),
  restoreLamenessEvent: (id) => api.post(`/lameness-events/${id}/restore/`),
  getLamenessFollowups: (params) => api.get('/lameness-followups/', { params }),
  getLamenessFollowup: (id) => api.get(`/lameness-followups/${id}/`),
  getLamenessFollowupHistory: (id) => api.get(`/lameness-followups/${id}/history/`),
  createLamenessFollowup: (data) => api.post('/lameness-followups/', data),
  updateLamenessFollowup: (id, data) => api.patch(`/lameness-followups/${id}/`, data),
  archiveLamenessFollowup: (id, data) => api.post(`/lameness-followups/${id}/archive/`, data || {}),
  restoreLamenessFollowup: (id) => api.post(`/lameness-followups/${id}/restore/`),
  importLamenessEventsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.update_existing === false) formData.append('update_existing', '0');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/lameness-events/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportLamenessEventsExcel: (params) => api.get('/lameness-events/export-excel/', {
    params,
    responseType: 'blob',
  }),
};

// Protocols API
export const protocolsAPI = {
  getProtocols: (params) => 
    api.get('/protocols/', { params }),
  
  getProtocol: (id) => 
    api.get(`/protocols/${id}/`),
  
  createProtocol: (data) => 
    api.post('/protocols/', data),
  
  updateProtocol: (id, data) => 
    api.put(`/protocols/${id}/`, data),
  
  deleteProtocol: (id) => 
    api.delete(`/protocols/${id}/`),
  
  duplicateProtocol: (id) => 
    api.post(`/protocols/${id}/duplicate/`),
  
  getProtocolsSelectList: () => 
    cachedGet('/protocols/select_list/', { cache: { ttl: CACHE_TTL.LONG } }),
};

// Treatments API
export const treatmentsAPI = {
  // Treatment Batches
  getBatches: (params) => 
    api.get('/treatment-batches/', { params }),
  
  getBatch: (id) => 
    api.get(`/treatment-batches/${id}/`),
  
  createBatch: (data) => 
    api.post('/treatment-batches/', data),

  updateBatch: (id, data) =>
    api.put(`/treatment-batches/${id}/`, data),
  
  deleteBatch: (id) => 
    api.delete(`/treatment-batches/${id}/`),

  // Treatments
  getTreatments: (params) => 
    api.get('/treatments/', { params }),
  
  getTreatment: (id) => 
    api.get(`/treatments/${id}/`),

  updateTreatment: (id, data) =>
    api.patch(`/treatments/${id}/`, data),

  completeTreatment: (id, notes = '') =>
    api.post(`/treatments/${id}/complete/`, { notes }),

  stopTreatment: (id, reason = '') =>
    api.post(`/treatments/${id}/stop/`, { reason }),

  pauseTreatment: (id, reason = '') =>
    api.post(`/treatments/${id}/pause/`, { reason }),

  resumeTreatment: (id) =>
    api.post(`/treatments/${id}/resume/`),

  bulkTransition: (treatmentIds, transition, note = '') =>
    api.post('/treatments/bulk_transition/', { treatment_ids: treatmentIds, transition, note }),
  
  removeTreatment: (id, reason) => 
    api.post(`/treatments/${id}/remove/`, { reason }),

  // Scheduled Actions
  getScheduledActions: (params) => 
    api.get('/scheduled-actions/', { params }),
  
  executeAction: (id, notes) => 
    api.post(`/scheduled-actions/${id}/execute/`, { notes }),
  
  skipAction: (id, reason) => 
    api.post(`/scheduled-actions/${id}/skip/`, { reason }),
  
  executeBatch: (actionIds, notes) => 
    api.post('/scheduled-actions/execute_batch/', { action_ids: actionIds, notes }),

  // Daily Plan
  getDailyPlan: (params) => 
    api.get('/daily-plan/', { params }),

  // Dashboard Stats
  getDashboardStats: (params) =>
    cachedGet('/dashboard-stats/', { params, cache: { ttl: CACHE_TTL.SHORT } }),

  // Estrus Events
  getEstrusEvents: (params) => 
    api.get('/estrus-events/', { params }),
  
  createEstrusEvent: (data) => 
    api.post('/estrus-events/', data),

  importEstrusEventsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post(`/estrus-events/import-excel/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Medication (group prescriptions)
  createMedicationBatch: (data) => api.post('/medication-batches/', data),
  getMedicationBatches: (params) => api.get('/medication-batches/', { params }),
  getMedicationBatch: (id) => api.get(`/medication-batches/${id}/`),
  updateMedicationBatch: (id, data) => api.patch(`/medication-batches/${id}/`, data),
  deleteMedicationBatch: (id) => api.delete(`/medication-batches/${id}/`),
  importMedicationBatchesExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.scope) formData.append('scope', options.scope);
    return api.post('/medication-batches/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importCalfTreatmentSheetExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.row_resolutions) formData.append('row_resolutions', JSON.stringify(options.row_resolutions));
    formData.append('scope', 'calf');
    return api.post('/medication-batches/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importCowTreatmentSheetExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.row_resolutions) formData.append('row_resolutions', JSON.stringify(options.row_resolutions));
    formData.append('scope', 'cow');
    return api.post('/medication-batches/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadCalfTreatmentSheetTemplate: () => api.get('/medication-batches/import-template/', { params: { scope: 'calf' }, responseType: 'blob' }),
  downloadCowTreatmentSheetTemplate: () => api.get('/medication-batches/import-template/', { params: { scope: 'cow' }, responseType: 'blob' }),
  downloadTreatmentImportReportPdf: (importBatch, scope) => api.get('/medication-batches/import-report-pdf/', { params: { import_batch: importBatch, scope }, responseType: 'blob' }),
  getCalfTreatmentBoard: (params) => api.get('/medication-batches/calf-board/', { params }),
  getCowTreatmentBoard: (params) => api.get('/medication-batches/cow-board/', { params }),
  searchCalfTreatmentCandidates: (params) => api.get('/medication-batches/calf-candidates/', { params }),
  exportCalfTreatmentDailyPdf: (params) => api.get('/medication-batches/calf-daily-pdf/', { params, responseType: 'blob' }),
  exportCowTreatmentDailyPdf: (params) => api.get('/medication-batches/cow-daily-pdf/', { params, responseType: 'blob' }),
  getCalfTreatmentInventorySnapshot: (params) => api.get('/medication-batches/calf-inventory-snapshot/', { params }),
  getCalfTreatmentFinancialReport: (params) => api.get('/medication-batches/calf-financial-report/', { params }),
  getCalfTreatmentHistory: async (params) => {
    try {
      return await api.get('/medication-batches/calf-treatment-history/', { params });
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 404 && status !== 405) throw error;
      return api.get('/estrus-events/calf-treatment-history/', { params });
    }
  },
  getCowTreatmentHistory: async (params) => {
    try {
      return await api.get('/medication-batches/cow-treatment-history/', { params });
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 404 && status !== 405) throw error;
      return api.get('/estrus-events/cow-treatment-history/', { params });
    }
  },
  getCowSurgeries: (params) => api.get('/cow-surgeries/', { params }),
  createCowSurgery: (data) => api.post('/cow-surgeries/', data),
  updateCowSurgery: (id, data) => api.patch(`/cow-surgeries/${id}/`, data),
  deleteCowSurgery: (id) => api.delete(`/cow-surgeries/${id}/`),
  getCowSurgeryTitles: (params) => api.get('/cow-surgery-titles/', { params }),
  createCowSurgeryTitle: (data) => api.post('/cow-surgery-titles/', data),
  updateCowSurgeryTitle: (id, data) => api.patch(`/cow-surgery-titles/${id}/`, data),
  deleteCowSurgeryTitle: (id) => api.delete(`/cow-surgery-titles/${id}/`),
  getCowSurgeryTypes: (params) => api.get('/cow-surgery-types/', { params }),
  createCowSurgeryType: (data) => api.post('/cow-surgery-types/', data),
  updateCowSurgeryType: (id, data) => api.patch(`/cow-surgery-types/${id}/`, data),
  deleteCowSurgeryType: (id) => api.delete(`/cow-surgery-types/${id}/`),
  getCowTreatmentInventorySnapshot: (params) => api.get('/medication-batches/cow-inventory-snapshot/', { params }),
  getCowTreatmentFinancialReport: (params) => api.get('/medication-batches/cow-financial-report/', { params }),
  getMedicationWithdrawalReport: (params) => api.get('/medication-batches/withdrawal-report/', { params }),
  exportMedicationWithdrawalReportExcel: (params) => api.get('/medication-batches/withdrawal-report/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getTreatmentOutcomeAnalytics: (params) => api.get('/medication-batches/outcome-analytics/', { params }),
  exportTreatmentOutcomeAnalyticsExcel: (params) => api.get('/medication-batches/outcome-analytics/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getCalfTreatmentPresets: (params) => api.get('/calf-treatment-presets/', { params }),
  createCalfTreatmentPreset: (data) => api.post('/calf-treatment-presets/', data),
  updateCalfTreatmentPreset: (id, data) => api.patch(`/calf-treatment-presets/${id}/`, data),
  deleteCalfTreatmentPreset: (id) => api.delete(`/calf-treatment-presets/${id}/`),
  getCowTreatmentPresets: (params) => api.get('/cow-treatment-presets/', { params }),
  createCowTreatmentPreset: (data) => api.post('/cow-treatment-presets/', data),
  updateCowTreatmentPreset: (id, data) => api.patch(`/cow-treatment-presets/${id}/`, data),
  deleteCowTreatmentPreset: (id) => api.delete(`/cow-treatment-presets/${id}/`),
  updateCalfPrescriptionSingle: (data) => api.post('/medication-batches/calf-prescription-update/', data),
  updateCowPrescriptionSingle: (data) => api.post('/medication-batches/cow-prescription-update/', data),
  deleteCalfPrescriptionSingle: (data) => api.post('/medication-batches/calf-prescription-delete/', data),
  deleteCowPrescriptionSingle: (data) => api.post('/medication-batches/cow-prescription-delete/', data),
  setCalfPrescriptionSheetVisibility: (data) => api.post('/medication-batches/calf-prescription-sheet-visibility/', data),
  setCowPrescriptionSheetVisibility: (data) => api.post('/medication-batches/cow-prescription-sheet-visibility/', data),
  updateCalfTreatmentCareStatus: (data) => api.post('/medication-batches/calf-care-status/', data),
  updateCowTreatmentCareStatus: (data) => api.post('/medication-batches/cow-care-status/', data),
  updateCalfTreatmentClinical: (data) => api.post('/medication-batches/calf-clinical-update/', data),
  updateCowTreatmentClinical: (data) => api.post('/medication-batches/cow-clinical-update/', data),
  updateCowTreatmentDailyExecution: (data) => api.post('/medication-batches/cow-daily-execution/', data),
  updateCowTreatmentBulkDailyExecution: (data) => api.post('/medication-batches/cow-bulk-daily-execution/', data),
  updateCalfTreatmentDailyExecution: (data) => api.post('/medication-batches/calf-daily-execution/', data),
  updateCalfTreatmentShiftBulkExecution: (data) => api.post('/medication-batches/calf-shift-bulk-execution/', data),
  recoverCalfTreatment: (data) => api.post('/medication-batches/calf-recover/', data),
  recoverCalfTreatmentBulk: (data) => api.post('/medication-batches/calf-recover-bulk/', data),
  closeCalfTreatmentCourse: (data) => api.post('/medication-batches/calf-close-course/', data),
  recoverCowTreatment: (data) => api.post('/medication-batches/cow-recover/', data),
  recoverCowTreatmentBulk: (data) => api.post('/medication-batches/cow-recover-bulk/', data),
  closeCowTreatmentCourse: (data) => api.post('/medication-batches/cow-close-course/', data),
};

// Reports API

// Core / audit API
export const coreAPI = {
  getAuditLogs: (params) => api.get('/core/audit-logs/', { params }),
  getDataQualityIssues: (params) => api.get('/core/data-quality-issues/', { params }),
  getDataQualityIssue: (id) => api.get(`/core/data-quality-issues/${id}/`),
  getDataQualitySummary: (params) => api.get('/core/data-quality-issues/summary/', { params }),
  getDataQualityRules: () => cachedGet('/core/data-quality-issues/rules/', { cache: { ttl: CACHE_TTL.MEDIUM } }),
  runDataQualityScan: (data) => api.post('/core/data-quality-issues/scan/', data || {}, { timeout: 900000 }),
  assignDataQualityIssue: (id, data) => api.post(`/core/data-quality-issues/${id}/assign/`, data),
  transitionDataQualityIssue: (id, data) => api.post(`/core/data-quality-issues/${id}/transition/`, data),
  commentDataQualityIssue: (id, data) => api.post(`/core/data-quality-issues/${id}/comment/`, data),
  getDataQualityIssueHistory: (id) => api.get(`/core/data-quality-issues/${id}/history/`),
  getDataQualityScanRuns: (params) => api.get('/core/data-quality-scan-runs/', { params }),
  exportDataQualityIssuesCsv: (params) => api.get('/core/data-quality-issues/export-csv/', { params, responseType: 'blob' }),
  resolveRecordForEdit: (params) => api.get('/core/record-editor/resolve/', { params }),
  updateRecordFromEditor: (data) => api.patch('/core/record-editor/update/', data),
  getUserDailyActivity: (params) => api.get('/core/audit-logs/daily-summary/', { params }),
  getImportJobs: (params) => api.get('/core/import-jobs/', { params }),
  getImportJob: (id) => api.get(`/core/import-jobs/${id}/`),
  getLatestImportJob: (params) => api.get('/core/import-jobs/latest/', { params }),
  downloadImportJobPdf: (id) => api.get(`/core/import-jobs/${id}/report-pdf/`, { responseType: 'blob' }),
  exportImportJobsPdf: (params) => api.get('/core/import-jobs/export-pdf/', { params, responseType: 'blob' }),

  // Alerts
  getAlerts: (params) => api.get('/core/alerts/', { params }),
  getFeatureCompatibility: () => api.get('/system/feature-compatibility/'),
  getSystemUpdateStatus: () => api.get('/system/update-status/'),
  getAlertsSummary: () => cachedGet('/core/alerts/summary/', { cache: { ttl: CACHE_TTL.SHORT } }),
  markAlertRead: (id) => api.post(`/core/alerts/${id}/mark_read/`),
  resolveAlert: (id) => api.post(`/core/alerts/${id}/resolve/`),
  markAllAlertsRead: () => api.post('/core/alerts/mark_all_read/'),
  generateAlerts: () => api.post('/core/alerts/generate/'),
  generateVetSmartAlerts: () => api.post('/core/alerts/generate-vet-smart/'),
  getVetSmartAlertsSummary: () => api.get('/core/alerts/vet-smart-summary/'),
  getVetCommandCenter: (params) => api.get('/core/alerts/vet-command-center/', { params }),
  getMilkHealthImpact: (params) => api.get('/cow-milk-records/health-impact/', { params, timeout: 900000 }),
  exportMilkHealthImpactExcel: (params) => api.get('/cow-milk-records/health-impact/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob', timeout: 900000 }),
  getFertilityHealthImpact: (params) => api.get('/repro/reports/fertility-health-impact/', { params, timeout: 900000 }),
  exportFertilityHealthImpactExcel: (params) => api.get('/repro/reports/fertility-health-impact/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob', timeout: 900000 }),

  // فهرست‌های ذخیره‌شده
  getSavedLists: (params) => api.get('/core/saved-lists/', { params }),
  getSavedList: (id) => api.get(`/core/saved-lists/${id}/`),
  createSavedList: (data) => api.post('/core/saved-lists/', data),
  updateSavedList: (id, data) => api.patch(`/core/saved-lists/${id}/`, data),
  deleteSavedList: (id) => api.delete(`/core/saved-lists/${id}/`),
  getSavedListTemplates: () => cachedGet('/core/saved-lists/templates/', { cache: { ttl: CACHE_TTL.MEDIUM } }),
  getSavedListMetadata: () => cachedGet('/core/saved-lists/metadata/', { cache: { ttl: CACHE_TTL.MEDIUM } }),
  runSavedListDefinition: (definition, limit) => api.post('/core/saved-lists/run-definition/', { definition, limit }),
  exportSavedListDefinition: (definition, limit) => api.post('/core/saved-lists/export-definition/', { definition, limit }, { responseType: 'blob' }),
  runSavedListTemplate: (templateKey, params) => api.get(`/core/saved-lists/run-template/${templateKey}/`, { params }),
  exportSavedListTemplate: (templateKey, params) => api.get(`/core/saved-lists/export-template/${templateKey}/`, { params, responseType: 'blob' }),
  saveSavedListTemplate: (templateKey, data) => api.post(`/core/saved-lists/save-template/${templateKey}/`, data),
  runSavedList: (id, params) => api.get(`/core/saved-lists/${id}/run/`, { params }),
  exportSavedList: (id, params) => api.get(`/core/saved-lists/${id}/export/`, { params, responseType: 'blob' }),

  // Alert Rules
  getAlertRules: (params) => api.get('/core/alert-rules/', { params }),
  createAlertRule: (data) => api.post('/core/alert-rules/', data),
  updateAlertRule: (id, data) => api.patch(`/core/alert-rules/${id}/`, data),

  // Vaccination Protocols
  getVaccinationProtocols: (params) => api.get('/core/vaccination-protocols/', { params }),
  getVaccinationProtocol: (id) => api.get(`/core/vaccination-protocols/${id}/`),
  createVaccinationProtocol: (data) => api.post('/core/vaccination-protocols/', data),
  updateVaccinationProtocol: (id, data) => api.patch(`/core/vaccination-protocols/${id}/`, data),
  deleteVaccinationProtocol: (id) => api.delete(`/core/vaccination-protocols/${id}/`),

  // Vaccination Steps
  getVaccinationSteps: (params) => api.get('/core/vaccination-steps/', { params }),
  createVaccinationStep: (data) => api.post('/core/vaccination-steps/', data),
  updateVaccinationStep: (id, data) => api.patch(`/core/vaccination-steps/${id}/`, data),
  deleteVaccinationStep: (id) => api.delete(`/core/vaccination-steps/${id}/`),

  // Calf Milk Feeding Protocols
  getCalfMilkFeedingProtocols: (params) => api.get('/core/calf-milk-protocols/', { params }),
  getCalfMilkFeedingProtocol: (id) => api.get(`/core/calf-milk-protocols/${id}/`),
  createCalfMilkFeedingProtocol: (data) => api.post('/core/calf-milk-protocols/', data),
  updateCalfMilkFeedingProtocol: (id, data) => api.patch(`/core/calf-milk-protocols/${id}/`, data),
  deleteCalfMilkFeedingProtocol: (id) => api.delete(`/core/calf-milk-protocols/${id}/`),
  getCalfMilkFeedingDashboard: (params) => cachedGet('/core/calf-milk-protocols/dashboard/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getCalfDailyTasks: (params) => cachedGet('/core/calf-daily-tasks/', { params, cache: { ttl: CACHE_TTL.SHORT } }),

  // Calf Milk Feeding Steps
  getCalfMilkFeedingSteps: (params) => api.get('/core/calf-milk-steps/', { params }),
  createCalfMilkFeedingStep: (data) => api.post('/core/calf-milk-steps/', data),
  updateCalfMilkFeedingStep: (id, data) => api.patch(`/core/calf-milk-steps/${id}/`, data),
  deleteCalfMilkFeedingStep: (id) => api.delete(`/core/calf-milk-steps/${id}/`),
};

export const reportsAPI = {
  getCanonicalKpiSummary: (params) => cachedGet('/reports/kpi/canonical-summary/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getKpiDefinitions: () => cachedGet('/reports/kpi/definitions/', { cache: { ttl: CACHE_TTL.LONG } }),
  exportCanonicalKpiSummary: (format, params) => api.get('/reports/kpi/export/canonical-summary/', {
    params: { ...(params || {}), format: format || 'xlsx' },
    responseType: 'blob',
  }),
  getPhase2Readiness: () => api.get('/reports/phase2-readiness/'),
  exportPhase2ReadinessExcel: () => api.get('/reports/phase2-readiness/', { params: { export: 'xlsx' }, responseType: 'blob' }),
  getLegacyReportCenter: () => cachedGet('/reports/legacy-center/', { cache: { ttl: CACHE_TTL.MEDIUM } }),
  getLegacyReportEndpoint: (endpoint, params) => cachedGet(endpoint.replace(/^\/api/, ''), { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportLegacyReportEndpoint: (endpoint, params) => api.get(endpoint.replace(/^\/api/, ''), { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getLegacyAdvancedCatalog: () => api.get('/reports/legacy-advanced/'),
  runLegacyAdvancedReport: (endpoint, params, responseType) => api.get(endpoint, { params, ...(responseType ? { responseType } : {}) }),
  getReportAnalyticsDashboard: () => api.get('/reports/analytics/dashboard/'),
  getReportCowDrilldown: (params) => api.get('/reports/analytics/cow-drilldown/', { params }),
  runDueReportSchedules: () => api.post('/reports/analytics/run-due-schedules/'),
  getAdvancedAnalyticsDashboard: () => api.get('/reports/analytics/executive-dashboard/', { timeout: 900000 }),
  getAdvancedCowTimeline: (params) => api.get('/reports/analytics/cow-timeline/', { params }),
  getAdvancedCowRiskScore: (params) => api.get('/reports/analytics/cow-risk-score/', { params }),
  buildAdvancedAnalyticsSnapshot: (data) => api.post('/reports/analytics/build-snapshot/', data || {}, { timeout: 900000 }),
  getFeedLegacyReportMap: (params) => cachedGet('/reports/feed-legacy-rpt-map/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportFeedLegacyReportMapExcel: (params) => api.get('/reports/feed-legacy-rpt-map/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  // NOTE: Some deployments namespace reports endpoints under /reports/.
  // Try /reports/admin/db-backup/ first, then fall back to /admin/db-backup/.
  downloadDbBackup: async (params) => {
    try {
      return await api.get('/reports/admin/db-backup/', {
        params,
        responseType: 'blob',
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 404) throw err;
      return await api.get('/admin/db-backup/', {
        params,
        responseType: 'blob',
      });
    }
  },

  // Persisted DB backups (admin-only)
  listDbBackups: (params) => api.get('/admin/db-backups/', { params }),
  createDbBackup: (data) => api.post('/admin/db-backups/', data),
  getDbBackup: (id) => api.get(`/admin/db-backups/${id}/`),
  runDbBackupJob: (id) => api.post(`/admin/db-backups/${id}/run/`),
  uploadDbBackupToBucket: (id) => api.post(`/admin/db-backups/${id}/upload/`),
  getBackupStorageSettings: () => api.get('/admin/db-backups/storage-settings/'),
  updateBackupStorageSettings: (data) => api.patch('/admin/db-backups/storage-settings/', data),
  deleteDbBackup: (id) => api.delete(`/admin/db-backups/${id}/`),
  downloadDbBackupById: (id) =>
    api.get(`/admin/db-backups/${id}/download/`, {
      responseType: 'blob',
    }),
  downloadDbBackupManifest: (id) =>
    api.get(`/admin/db-backups/${id}/manifest/`, { responseType: 'blob' }),
  downloadDbBackupPart: (backupId, partId) =>
    api.get(`/admin/db-backups/${backupId}/parts/${partId}/download/`, { responseType: 'blob' }),

  getDailyPlanExcel: (params) => 
    api.get('/reports/daily-plan/excel/', { 
      params,
      responseType: 'blob' 
    }),
  
  getDailyPlanPDF: (params) => 
    api.get('/reports/daily-plan/pdf/', { 
      params,
      responseType: 'blob' 
    }),
  
  getTreatmentReport: (params) => 
    api.get('/reports/treatments/', { params }),

  getVetHealthSummary: (params) => cachedGet('/reports/vet-health-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  getUdderLamenessSummary: (params) => cachedGet('/reports/udder-lameness-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportUdderLamenessSummaryExcel: (params) => api.get('/reports/udder-lameness-summary/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  exportUdderLamenessSummaryPDF: (params) => api.get('/reports/udder-lameness-summary/', {
    params: withPdfExportParams(params),
    responseType: 'blob',
  }),
  getUdderLamenessOperations: (params) => cachedGet('/reports/udder-lameness-operations/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportUdderLamenessOperationsExcel: (params) => api.get('/reports/udder-lameness-operations/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  exportUdderLamenessOperationsPDF: (params) => api.get('/reports/udder-lameness-operations/', {
    params: withPdfExportParams(params),
    responseType: 'blob',
  }),
  exportCowUdderLamenessCardPDF: (cowId) => api.get(`/reports/cows/${cowId}/udder-lameness-card/pdf/`, {
    responseType: 'blob',
  }),
  exportHealthHistoryExcel: (params) => api.get('/reports/health-history-export/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  exportHealthHistoryPDF: (params) => api.get('/reports/health-history-export/', {
    params: withPdfExportParams(params),
    responseType: 'blob',
  }),
  exportVetHealthSummaryExcel: (params) => api.get('/reports/vet-health-summary/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  exportVetHealthSummaryPDF: (params) => api.get('/reports/vet-health-summary/', {
    params: withPdfExportParams(params),
    responseType: 'blob',
  }),
  getVetPhase2Summary: (params) => cachedGet('/reports/vet-phase2-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportVetPhase2SummaryExcel: (params) => api.get('/reports/vet-phase2-summary/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  exportVetPhase2SummaryPDF: (params) => api.get('/reports/vet-phase2-summary/', {
    params: withPdfExportParams(params),
    responseType: 'blob',
  }),

  getFeedInventorySummary: (params) => cachedGet('/reports/feed-inventory-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportFeedInventorySummaryExcel: (params) => api.get('/reports/feed-inventory-summary/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getFeedOperationalCost: (params) => cachedGet('/reports/feed-operational-cost/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportFeedOperationalCostExcel: (params) => api.get('/reports/feed-operational-cost/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getFeedDashboard: (params) => cachedGet('/reports/feed-dashboard/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportFeedDashboardExcel: (params) => api.get('/reports/feed-dashboard/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getFeedIOFC: (params) => cachedGet('/reports/feed-iofc/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  rebuildFeedEconomicsSnapshots: async (data) => {
    const response = await api.post('/reports/feed-economics-snapshots/rebuild/', data);
    clearBrowserApiCache();
    return response;
  },
  exportFeedIOFCExcel: (params) => api.get('/reports/feed-iofc/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getFeedPlannedConsumptionReport: (params) => cachedGet('/reports/feed-planned-consumption/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedActualConsumptionReport: (params) => cachedGet('/reports/feed-actual-consumption/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedVarianceReport: (params) => cachedGet('/reports/feed-variance/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedWasteReport: (params) => cachedGet('/reports/feed-waste/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedLossReport: (params) => cachedGet('/reports/feed-loss/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedResidualReport: (params) => cachedGet('/reports/feed-residual/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedOverheadReport: (params) => cachedGet('/reports/feed-overhead/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getFeedCostPriceReport: (params) => cachedGet('/reports/feed-cost-price/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getInventoryAlerts: (params) => cachedGet('/reports/inventory-alerts/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportInventoryAlertsExcel: (params) => api.get('/reports/inventory-alerts/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getPhase3OperationalSummary: (params) => cachedGet('/reports/phase3-operational-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase3OperationalSummaryExcel: (params) => api.get('/reports/phase3-operational-summary/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getPhase4ExecutiveDashboard: (params) => cachedGet('/reports/phase4-executive-dashboard/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase4ExecutiveDashboardExcel: (params) => api.get('/reports/phase4-executive-dashboard/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getPhase4ManagementCostAnalysis: (params) => cachedGet('/reports/phase4-management-cost-analysis/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase4ManagementCostAnalysisExcel: (params) => api.get('/reports/phase4-management-cost-analysis/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getPhase4ProfitabilityAnalysis: (params) => cachedGet('/reports/phase4-profitability-analysis/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase4ProfitabilityAnalysisExcel: (params) => api.get('/reports/phase4-profitability-analysis/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getPhase4ManagementDrilldown: (params) => cachedGet('/reports/phase4-management-drilldown/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase4ManagementDrilldownExcel: (params) => api.get('/reports/phase4-management-drilldown/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),
  getReportPhaseMap: () => cachedGet('/reports/phase-map/', { cache: { ttl: CACHE_TTL.LONG } }),
  exportReportPhaseMapPDF: () => api.get('/reports/phase-map/', { params: { export: 'pdf' }, responseType: 'blob' }),
  getCalfReportCommandCenter: (params) => cachedGet('/reports/calf-command-center/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportCalfReportCommandCenterExcel: (params) => api.get('/reports/calf-command-center/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportCalfReportCommandCenterPDF: (params) => api.get('/reports/calf-command-center/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getHerdExecutiveDashboard: (params) => cachedGet('/reports/herd-executive/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportHerdExecutiveDashboardExcel: (params) => api.get('/reports/herd-executive/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportHerdExecutiveDashboardPDF: (params) => api.get('/reports/herd-executive/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase1OperationalReports: (params) => cachedGet('/reports/operational/phase1/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase1OperationalReportsExcel: (params) => api.get('/reports/operational/phase1/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase1OperationalReportsPDF: (params) => api.get('/reports/operational/phase1/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase2OperationalFertilityReports: (params) => cachedGet('/reports/operational/phase2/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase2OperationalFertilityReportsExcel: (params) => api.get('/reports/operational/phase2/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase2OperationalFertilityReportsPDF: (params) => api.get('/reports/operational/phase2/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase1AnalyticalReports: (params) => cachedGet('/reports/analytical/phase1/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase1AnalyticalReportsExcel: (params) => api.get('/reports/analytical/phase1/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase1AnalyticalReportsPDF: (params) => api.get('/reports/analytical/phase1/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase4FertilityAnalytics: (params) => cachedGet('/reports/analytical/fertility/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase4FertilityAnalyticsExcel: (params) => api.get('/reports/analytical/fertility/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase4FertilityAnalyticsPDF: (params) => api.get('/reports/analytical/fertility/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase5ProductionHealthCalfAnalytics: (params) => cachedGet('/reports/analytical/production-health-calf/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase5ProductionHealthCalfAnalyticsExcel: (params) => api.get('/reports/analytical/production-health-calf/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase5ProductionHealthCalfAnalyticsPDF: (params) => api.get('/reports/analytical/production-health-calf/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase6BaharbandPeopleQualityAnalytics: (params) => cachedGet('/reports/analytical/baharband-people-quality/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase6BaharbandPeopleQualityAnalyticsExcel: (params) => api.get('/reports/analytical/baharband-people-quality/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase6BaharbandPeopleQualityAnalyticsPDF: (params) => api.get('/reports/analytical/baharband-people-quality/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase24LactationHeiferAnalytics: (params) => cachedGet('/reports/analytical/lactation-heifer/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase24LactationHeiferAnalyticsExcel: (params) => api.get('/reports/analytical/lactation-heifer/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase24LactationHeiferAnalyticsPDF: (params) => api.get('/reports/analytical/lactation-heifer/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getHeiferServiceAgeGestation: (params) => cachedGet('/reports/analytical/heifer-service-age-gestation/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportHeiferServiceAgeGestationExcel: (params) => api.get('/reports/analytical/heifer-service-age-gestation/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportHeiferServiceAgeGestationPDF: (params) => api.get('/reports/analytical/heifer-service-age-gestation/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase22CalvingTransitionAnalytics: (params) => cachedGet('/reports/analytical/calving-transition/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase22CalvingTransitionAnalyticsExcel: (params) => api.get('/reports/analytical/calving-transition/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase22CalvingTransitionAnalyticsPDF: (params) => api.get('/reports/analytical/calving-transition/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase7ManagementExecutiveReport: (params) => cachedGet('/reports/management/executive-summary/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportPhase7ManagementExecutiveReportExcel: (params) => api.get('/reports/management/executive-summary/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase7ManagementExecutiveReportPDF: (params) => api.get('/reports/management/executive-summary/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  getPhase9SmartAlertCenter: (params) => cachedGet('/reports/smart-alert-center/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase9SmartAlertCenterExcel: (params) => api.get('/reports/smart-alert-center/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase9SmartAlertCenterPDF: (params) => api.get('/reports/smart-alert-center/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  syncPhase9SmartAlerts: (params) => api.post('/reports/smart-alert-center/', { action: 'sync_alerts' }, { params }),
  queuePhase9SmartAlertSMS: (params, data) => api.post('/reports/smart-alert-center/', { action: 'queue_sms', ...(data || {}) }, { params }),
  getPhase10ManagementSettings: () => cachedGet('/reports/management-settings/', { cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase10ManagementSettingsExcel: () => api.get('/reports/management-settings/', {
    params: { export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase10ManagementSettingsPDF: () => api.get('/reports/management-settings/', {
    params: { export: 'pdf' },
    responseType: 'blob',
  }),
  applyPhase10Defaults: () => api.post('/reports/management-settings/', { action: 'apply_defaults' }),
  savePhase10Targets: (targets) => api.post('/reports/management-settings/', { action: 'save_targets', targets }),
  savePhase10AlertRules: (alert_rules) => api.post('/reports/management-settings/', { action: 'save_alert_rules', alert_rules }),
  savePhase10ReportDisplay: (data) => api.post('/reports/management-settings/', { action: 'save_report_display', ...(data || {}) }),
  savePhase10SMSSettings: (data) => api.post('/reports/management-settings/', { action: 'save_sms_settings', ...(data || {}) }),
  testPhase10SMS: (data) => api.post('/reports/management-settings/', { action: 'test_sms', ...(data || {}) }),
  getManagementDecisionAnalytics: (params) => cachedGet('/reports/management-decision-analytics/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  createManagementDecisionTask: (data) => api.post('/reports/management-decision-analytics/', { action: 'create_task', ...(data || {}) }),
  getAnalyticsLab: () => cachedGet('/reports/analytics-lab/', { cache: { ttl: CACHE_TTL.SHORT } }),
  runAnalyticsLab: (config) => api.post('/reports/analytics-lab/', { action: 'run', config }),
  drilldownAnalyticsLab: (config, row, limit = 500) => api.post('/reports/analytics-lab/', { action: 'drilldown', config, row, limit }),
  saveAnalyticsLabAnalysis: (data) => api.post('/reports/analytics-lab/', { action: 'save', ...(data || {}) }),
  deleteAnalyticsLabAnalysis: (id) => api.post('/reports/analytics-lab/', { action: 'delete', id }),
  cloneAnalyticsLabAnalysis: (id, title) => api.post('/reports/analytics-lab/', { action: 'clone', id, title }),
  exportAnalyticsLab: (config, format = 'xlsx', title = '') => api.post('/reports/analytics-lab/', { action: 'export', config, format, title }, { responseType: 'blob', timeout: 900000 }),
  getPhase11CustomReportBuilder: () => cachedGet('/reports/custom-builder/', { cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase11CustomReportBuilderExcel: () => api.get('/reports/custom-builder/', {
    params: { export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase11CustomReportBuilderPDF: () => api.get('/reports/custom-builder/', {
    params: { export: 'pdf' },
    responseType: 'blob',
  }),
  seedPhase11CustomReports: () => api.post('/reports/custom-builder/', { action: 'seed_defaults' }),
  savePhase11CustomView: (data) => api.post('/reports/custom-builder/', { action: 'save_view', ...(data || {}) }),
  deletePhase11CustomView: (id) => api.post('/reports/custom-builder/', { action: 'delete_view', id }),
  runPhase11CustomView: (id, data) => api.post('/reports/custom-builder/', { action: 'run_view', id, ...(data || {}) }),
  clonePhase11Report: (data) => api.post('/reports/custom-builder/', { action: 'clone_report', ...(data || {}) }),
  savePhase11Dashboard: (data) => api.post('/reports/custom-builder/', { action: 'save_dashboard', ...(data || {}) }),
  runPhase11Dashboard: (id, data) => api.post('/reports/custom-builder/', { action: 'run_dashboard', id, ...(data || {}) }),
  clonePhase11Dashboard: (id, title) => api.post('/reports/custom-builder/', { action: 'clone_dashboard', id, title }),
  deletePhase11Dashboard: (id) => api.post('/reports/custom-builder/', { action: 'delete_dashboard', id }),
  getPhase12ReportPerformanceCenter: () => cachedGet('/reports/performance-center/', { cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase12ReportPerformanceExcel: () => api.get('/reports/performance-center/', {
    params: { export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase12ReportPerformancePDF: () => api.get('/reports/performance-center/', {
    params: { export: 'pdf' },
    responseType: 'blob',
  }),
  warmPhase12DynamicReports: (data) => api.post('/reports/performance-center/', { action: 'warm_dynamic_reports', ...(data || {}) }),
  clearPhase12ExpiredCache: () => api.post('/reports/performance-center/', { action: 'clear_expired_cache' }),
  createPhase12ExportJob: (data) => api.post('/reports/performance-center/', { action: 'create_export_job', ...(data || {}) }),
  runPhase12BackgroundJob: (jobId) => api.post('/reports/performance-center/', { action: 'run_job', id: jobId }),
  getPhase14EconomicDecisionAnalytics: (params) => cachedGet('/reports/management/economic-decision/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase14EconomicDecisionAnalyticsExcel: (params) => api.get('/reports/management/economic-decision/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase14EconomicDecisionAnalyticsPDF: (params) => api.get('/reports/management/economic-decision/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  markPhase14CullReviewCandidates: (params, cowIds) => api.post('/reports/management/economic-decision/', { action: 'mark_cull_review_candidates', cow_ids: cowIds }, { params }),
  getPhase15PredictiveRiskAnalytics: (params) => cachedGet('/reports/predictive-risk/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportPhase15PredictiveRiskExcel: (params) => api.get('/reports/predictive-risk/', {
    params: { ...(params || {}), export: 'xlsx' },
    responseType: 'blob',
  }),
  exportPhase15PredictiveRiskPDF: (params) => api.get('/reports/predictive-risk/', {
    params: { ...(params || {}), export: 'pdf' },
    responseType: 'blob',
  }),
  markPhase15SpecialMonitor: (params, cowIds) => api.post('/reports/predictive-risk/', { action: 'mark_special_monitor', cow_ids: cowIds }, { params }),
  getEconomicCowValue: (cowId, params) => cachedGet(`/reports/economic/cow-value/${cowId}/`, { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getEconomicCullCandidates: (params) => cachedGet('/reports/economic/cull-candidates/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getEconomicSummary: (params) => cachedGet('/reports/economic/summary/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  applyEconomicDecision: (cowId, data) => api.post(`/reports/economic/cow-value/${cowId}/apply-decision/`, data),
  bulkApplyEconomicDecision: (data) => api.post('/reports/economic/bulk-apply-decision/', data),
  getEconomicDecisionHistory: (cowId, params) => cachedGet(`/reports/economic/cow-value/${cowId}/decision-history/`, { params, cache: { ttl: CACHE_TTL.SHORT } }),
  getEconomicDecisionWorkflow: (cowId, params) => cachedGet(`/reports/economic/cow-value/${cowId}/workflow/`, { params, cache: { ttl: CACHE_TTL.SHORT } }),
  updateEconomicDecisionWorkflow: (cowId, data) => api.post(`/reports/economic/cow-value/${cowId}/workflow/`, data),
  // Phase 30 - governed predictive models, explainability, evaluation and drift
  getPredictiveModelCenter: (params) => api.get('/reports/predictive-models/', { params }),
  runPredictiveModels: (data) => api.post('/reports/predictive-models/run/', data),
  seedPredictiveModels: () => api.post('/reports/predictive-models/seed/', {}),
  evaluatePredictiveModels: (data) => api.post('/reports/predictive-models/evaluate/', data),
  calculatePredictiveModelDrift: (data) => api.post('/reports/predictive-models/drift/', data),
  getPredictiveModelDefinitions: (params) => api.get('/reports/predictive-model-definitions/', { params }),
  updatePredictiveModelDefinition: (id, data) => api.patch(`/reports/predictive-model-definitions/${id}/`, data),
  getPredictiveModelVersions: (params) => api.get('/reports/predictive-model-versions/', { params }),
  activatePredictiveModelVersion: (id) => api.post(`/reports/predictive-model-versions/${id}/activate/`, {}),
  disablePredictiveModelVersion: (id, reason) => api.post(`/reports/predictive-model-versions/${id}/disable/`, { reason }),
  getPredictionRuns: (params) => api.get('/reports/prediction-runs/', { params }),
  getPredictionResults: (params) => api.get('/reports/prediction-results/', { params }),
  updatePredictionResult: (id, data) => api.patch(`/reports/prediction-results/${id}/`, data),
  getModelEvaluations: (params) => api.get('/reports/model-evaluations/', { params }),
  getModelDrift: (params) => api.get('/reports/model-drift/', { params }),
  exportPredictiveRun: (id, format = 'xlsx') => api.get(`/reports/predictive-models/runs/${id}/export/`, { params: { format }, responseType: 'blob' }),
  // Phase 29 - management scenario analysis and decision support
  getManagementScenarioCenter: (params) => api.get('/reports/management-scenarios/', { params }),
  evaluateManagementScenario: (data) => api.post('/reports/management-scenarios/evaluate/', data),
  seedManagementScenarioTemplates: () => api.post('/reports/management-scenarios/seed-templates/', {}),
  getManagementScenarioTemplates: (params) => api.get('/reports/management-scenario-templates/', { params }),
  createManagementScenarioTemplate: (data) => api.post('/reports/management-scenario-templates/', data),
  updateManagementScenarioTemplate: (id, data) => api.patch(`/reports/management-scenario-templates/${id}/`, data),
  getManagementScenarioRuns: (params) => api.get('/reports/management-scenario-runs/', { params }),
  getManagementScenarioRun: (id) => api.get(`/reports/management-scenario-runs/${id}/`),
  exportManagementScenarioExcel: (id) => api.get(`/reports/management-scenarios/${id}/export/`, { params: { format: 'xlsx' }, responseType: 'blob' }),
  exportManagementScenarioPDF: (id) => api.get(`/reports/management-scenarios/${id}/export/`, { params: { format: 'pdf' }, responseType: 'blob' }),
  exportManagementScenarioCSV: (id) => api.get(`/reports/management-scenarios/${id}/export/`, { params: { format: 'csv' }, responseType: 'blob' }),
  // Phase 28 - alert engine and corrective actions
  getCorrectiveAlertCenter: (params) => api.get('/reports/corrective-alert-center/', { params }),
  exportCorrectiveAlertCenterExcel: (params) => api.get('/reports/corrective-alert-center/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportCorrectiveAlertCenterPDF: (params) => api.get('/reports/corrective-alert-center/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  exportCorrectiveAlertCenterCSV: (params) => api.get('/reports/corrective-alert-center/', { params: { ...(params || {}), export: 'csv' }, responseType: 'blob' }),
  evaluateCorrectiveAlerts: (data) => api.post('/reports/corrective-alert-center/evaluate/', data),
  seedCorrectiveAlertRules: (data) => api.post('/reports/corrective-alert-center/seed-rules/', data || {}),
  getCorrectiveAlertRules: (params) => api.get('/reports/corrective-alert-rules/', { params }),
  createCorrectiveAlertRule: (data) => api.post('/reports/corrective-alert-rules/', data),
  updateCorrectiveAlertRule: (id, data) => api.patch(`/reports/corrective-alert-rules/${id}/`, data),
  getCorrectiveAlertCases: (params) => api.get('/reports/corrective-alert-cases/', { params }),
  updateCorrectiveAlertCase: (id, data) => api.patch(`/reports/corrective-alert-cases/${id}/`, data),
  acknowledgeCorrectiveAlertCase: (id) => api.post(`/reports/corrective-alert-cases/${id}/acknowledge/`, {}),
  sendCorrectiveAlertCaseSMS: (id, data) => api.post(`/reports/corrective-alert-cases/${id}/send-sms/`, data || {}),
  getCorrectiveActions: (params) => api.get('/reports/corrective-actions/', { params }),
  createCorrectiveAction: (data) => api.post('/reports/corrective-actions/', data),
  updateCorrectiveAction: (id, data) => api.patch(`/reports/corrective-actions/${id}/`, data),
  getCorrectiveAlertRuns: (params) => api.get('/reports/alert-evaluation-runs/', { params }),
  // Phase 27 - pen capacity and herd flow
  getHerdCapacityFlow: (params) => api.get('/reports/herd-capacity-flow/', { params }),
  exportHerdCapacityFlowExcel: (params) => api.get('/reports/herd-capacity-flow/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportHerdCapacityFlowPDF: (params) => api.get('/reports/herd-capacity-flow/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  exportHerdCapacityFlowCSV: (params) => api.get('/reports/herd-capacity-flow/', { params: { ...(params || {}), export: 'csv' }, responseType: 'blob' }),
  rebuildHerdCapacityFlow: (data) => api.post('/reports/herd-capacity-flow/rebuild/', data),
  getCapacityPolicies: (params) => api.get('/reports/capacity-policies/', { params }),
  createCapacityPolicy: (data) => api.post('/reports/capacity-policies/', data),
  updateCapacityPolicy: (id, data) => api.patch(`/reports/capacity-policies/${id}/`, data),
  getCapacitySnapshots: (params) => api.get('/reports/capacity-snapshots/', { params }),
  getHerdFlowForecasts: (params) => api.get('/reports/herd-flow-forecasts/', { params }),
  getTransferRecommendations: (params) => api.get('/reports/transfer-recommendations/', { params }),
  updateTransferRecommendation: (id, data) => api.patch(`/reports/transfer-recommendations/${id}/`, data),
  // Phase 26 - economic culling, survival and replacement
  getCullingReplacement: (params) => api.get('/reports/culling-replacement/', { params }),
  exportCullingReplacementExcel: (params) => api.get('/reports/culling-replacement/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportCullingReplacementPDF: (params) => api.get('/reports/culling-replacement/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  exportCullingReplacementCSV: (params) => api.get('/reports/culling-replacement/', { params: { ...(params || {}), export: 'csv' }, responseType: 'blob' }),
  rebuildCullingReplacement: (data) => api.post('/reports/culling-replacement/rebuild/', data),
  getExitClassifications: (params) => api.get('/reports/exit-classifications/', { params }),
  updateExitClassification: (id, data) => api.patch(`/reports/exit-classifications/${id}/`, data),
  getCowValueSnapshots: (params) => api.get('/reports/cow-value-snapshots/', { params }),
  getReplacementPlanSnapshots: (params) => api.get('/reports/replacement-plan-snapshots/', { params }),
  // Phase 25 - central financial engine
  getFinancialEngine: (params) => api.get('/reports/financial-engine/', { params }),
  exportFinancialEngineExcel: (params) => api.get('/reports/financial-engine/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportFinancialEnginePDF: (params) => api.get('/reports/financial-engine/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  exportFinancialEngineCSV: (params) => api.get('/reports/financial-engine/', { params: { ...(params || {}), export: 'csv' }, responseType: 'blob' }),
  rebuildFinancialEngine: (data) => api.post('/reports/financial-engine/rebuild/', data),
  getFinancialPeriods: (params) => api.get('/finance/periods/', { params }),
  createFinancialPeriod: (data) => api.post('/finance/periods/', data),
  updateFinancialPeriod: (id, data) => api.patch(`/finance/periods/${id}/`, data),
  deleteFinancialPeriod: (id) => api.delete(`/finance/periods/${id}/`),
  closeFinancialPeriod: (id) => api.post(`/finance/periods/${id}/close/`, {}),
  reopenFinancialPeriod: (id) => api.post(`/finance/periods/${id}/reopen/`, {}),
  getFinancialCategories: (params) => api.get('/finance/categories/', { params }),
  createFinancialCategory: (data) => api.post('/finance/categories/', data),
  updateFinancialCategory: (id, data) => api.patch(`/finance/categories/${id}/`, data),
  deleteFinancialCategory: (id) => api.delete(`/finance/categories/${id}/`),
  getHistoricalPrices: (params) => api.get('/finance/historical-prices/', { params }),
  createHistoricalPrice: (data) => api.post('/finance/historical-prices/', data),
  updateHistoricalPrice: (id, data) => api.patch(`/finance/historical-prices/${id}/`, data),
  deleteHistoricalPrice: (id) => api.delete(`/finance/historical-prices/${id}/`),
  getFinancialBudgets: (params) => api.get('/finance/budgets/', { params }),
  createFinancialBudget: (data) => api.post('/finance/budgets/', data),
  updateFinancialBudget: (id, data) => api.patch(`/finance/budgets/${id}/`, data),
  deleteFinancialBudget: (id) => api.delete(`/finance/budgets/${id}/`),
  approveFinancialBudget: (id) => api.post(`/finance/budgets/${id}/approve/`, {}),
  getFinancialBudgetLines: (params) => api.get('/finance/budget-lines/', { params }),
  createFinancialBudgetLine: (data) => api.post('/finance/budget-lines/', data),
  updateFinancialBudgetLine: (id, data) => api.patch(`/finance/budget-lines/${id}/`, data),
  deleteFinancialBudgetLine: (id) => api.delete(`/finance/budget-lines/${id}/`),
  getFinancialAllocationRuns: (params) => api.get('/finance/allocation-runs/', { params }),
  getFinancialAllocationLines: (params) => api.get('/finance/allocation-lines/', { params }),
  getFinancialSnapshots: (params) => api.get('/finance/daily-snapshots/', { params }),
  getFinancialReconciliations: (params) => api.get('/finance/reconciliation-runs/', { params }),
  getPhase4CostCenters: (params) => api.get('/phase4/cost-centers/', { params }),
  createPhase4CostCenter: (data) => api.post('/phase4/cost-centers/', data),
  updatePhase4CostCenter: (id, data) => api.patch(`/phase4/cost-centers/${id}/`, data),
  deletePhase4CostCenter: (id) => api.delete(`/phase4/cost-centers/${id}/`),
  importPhase4CostCentersExcel: ({ file, validate_only = false, source = 'excel', import_batch = '' }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', validate_only ? 'true' : 'false');
    formData.append('source', source);
    if (import_batch) formData.append('import_batch', import_batch);
    return api.post('/phase4/cost-centers/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPhase4OperatingExpenses: (params) => api.get('/phase4/operating-expenses/', { params }),
  createPhase4OperatingExpense: (data) => api.post('/phase4/operating-expenses/', data),
  updatePhase4OperatingExpense: (id, data) => api.patch(`/phase4/operating-expenses/${id}/`, data),
  deletePhase4OperatingExpense: (id) => api.delete(`/phase4/operating-expenses/${id}/`),
  importPhase4OperatingExpensesExcel: ({ file, validate_only = false, source = 'excel', import_batch = '' }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', validate_only ? 'true' : 'false');
    formData.append('source', source);
    if (import_batch) formData.append('import_batch', import_batch);
    return api.post('/phase4/operating-expenses/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPhase4RevenueEntries: (params) => api.get('/phase4/revenue-entries/', { params }),
  createPhase4RevenueEntry: (data) => api.post('/phase4/revenue-entries/', data),
  updatePhase4RevenueEntry: (id, data) => api.patch(`/phase4/revenue-entries/${id}/`, data),
  deletePhase4RevenueEntry: (id) => api.delete(`/phase4/revenue-entries/${id}/`),
  getPhase4MilkSales: (params) => api.get('/phase4/milk-sales/', { params }),
  createPhase4MilkSale: (data) => api.post('/phase4/milk-sales/', data),
  updatePhase4MilkSale: (id, data) => api.patch(`/phase4/milk-sales/${id}/`, data),
  deletePhase4MilkSale: (id) => api.delete(`/phase4/milk-sales/${id}/`),
  importPhase4MilkSalesExcel: ({ file, validate_only = false, source = 'excel', import_batch = '' }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', validate_only ? 'true' : 'false');
    formData.append('source', source);
    if (import_batch) formData.append('import_batch', import_batch);
    return api.post('/phase4/milk-sales/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPhase4LivestockSales: (params) => api.get('/phase4/livestock-sales/', { params }),
  createPhase4LivestockSale: (data) => api.post('/phase4/livestock-sales/', data),
  updatePhase4LivestockSale: (id, data) => api.patch(`/phase4/livestock-sales/${id}/`, data),
  deletePhase4LivestockSale: (id) => api.delete(`/phase4/livestock-sales/${id}/`),
  importPhase4RevenueEntriesExcel: ({ file, validate_only = false, source = 'excel', import_batch = '' }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', validate_only ? 'true' : 'false');
    formData.append('source', source);
    if (import_batch) formData.append('import_batch', import_batch);
    return api.post('/phase4/revenue-entries/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPhase4AllocationRules: (params) => api.get('/phase4/allocation-rules/', { params }),
  createPhase4AllocationRule: (data) => api.post('/phase4/allocation-rules/', data),
  updatePhase4AllocationRule: (id, data) => api.patch(`/phase4/allocation-rules/${id}/`, data),
  deletePhase4AllocationRule: (id) => api.delete(`/phase4/allocation-rules/${id}/`),
  importPhase4AllocationRulesExcel: ({ file, validate_only = false, source = 'excel', import_batch = '' }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', validate_only ? 'true' : 'false');
    formData.append('source', source);
    if (import_batch) formData.append('import_batch', import_batch);
    return api.post('/phase4/allocation-rules/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Reproduction API (Service, Pregnancy Check, Vet Visit, KPI)
export const reproductionAPI = {
  // Semen inventory
  getSemen: (params) => api.get('/repro/semen/', { params }),

  getSemenLots: (params) => api.get('/repro/semen-lots/', { params }),
  getSemenMovements: (params) => api.get('/repro/semen-movements/', { params }),
  createSemenLot: (data) => api.post('/repro/semen-lots/', data),
  updateSemenLot: (id, data) => api.patch(`/repro/semen-lots/${id}/`, data),
  syncSemenStockCounters: (data = {}) => api.post('/repro/semen-lots/sync-counters/', data),
  createSemenMovement: (data) => api.post('/repro/semen-movements/', data),
  transferSemenLots: (data) => api.post('/repro/semen-movements/transfer/', data),

  // Report: semen usage
  getSemenUsageReport: (params) => cachedGet('/repro/reports/semen-usage/', { params, cache: { ttl: CACHE_TTL.MEDIUM } }),
  exportSemenUsageReportExcel: (params) => api.get('/repro/reports/semen-usage/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),

  // Report: low stock
  getSemenLowStockReport: (params) => cachedGet('/repro/reports/semen-low-stock/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportSemenLowStockExcel: (params) => api.get('/repro/reports/semen-low-stock/', {
    params: withXlsxExportParams(params),
    responseType: 'blob',
  }),

  // Import templates & excel
  downloadSemenLotsTemplate: () => api.get('/repro/semen-lots/template/', { responseType: 'blob' }),
  downloadSemenMovementsTemplate: () => api.get('/repro/semen-movements/template/', { responseType: 'blob' }),

  importSemenLotsExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/repro/semen-lots/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importSemenMovementsExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/repro/semen-movements/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Cow semen plan (3 priorities)
  getCowSemenPlan: (cowId) => api.get(`/repro/semen-plans/cow/${cowId}/`),
  upsertCowSemenPlan: (cowId, data) => api.post(`/repro/semen-plans/cow/${cowId}/`, data),
  downloadCowSemenPlansTemplate: () => api.get('/repro/semen-plans/template/', { responseType: 'blob' }),
  downloadCowSemenImportReportPdf: (importBatch) => api.get('/repro/semen-plans/import-report-pdf/', {
    params: { import_batch: importBatch },
    responseType: 'blob',
  }),
  importCowSemenPlansExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const resolutionMap = options?.cow_resolution_map;
    if (resolutionMap && typeof resolutionMap === 'object' && !Array.isArray(resolutionMap) && typeof resolutionMap.preventDefault !== 'function') {
      formData.append('cow_resolution_map', JSON.stringify(resolutionMap));
    }
    if (options?.validate_only) {
      formData.append('validate_only', '1');
    }
    return api.post('/repro/semen-plans/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Service Events
  getServices: (params) => api.get('/repro/services/', { params }),
  createService: (data) => api.post('/repro/services/', data),
  updateService: (id, data) => api.patch(`/repro/services/${id}/`, data),
  getInseminationSkips: (params) => api.get('/repro/insemination-skips/', { params }),
  createInseminationSkip: (data) => api.post('/repro/insemination-skips/', data),
  downloadServicesTemplate: () => api.get('/repro/services/template/', { responseType: 'blob' }),
  importServicesExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.download_pdf_report) formData.append('download_pdf_report', '1');
    return api.post('/repro/services/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: options.download_pdf_report ? 'blob' : 'json',
    });
  },
  downloadServiceImportReportPdf: (importBatch) => api.get('/repro/services/import-report-pdf/', {
    params: { import_batch: importBatch },
    responseType: 'blob',
  }),
  getServiceImportHistory: (params) => api.get('/repro/services/import-history/', { params }),
  deleteServiceImportBatch: (importBatch) => api.post('/repro/services/delete-import-batch/', { import_batch: importBatch }),

  // Abortions
  getAbortions: (params) => api.get('/repro/abortions/', { params }),
  createAbortion: (data) => api.post('/repro/abortions/', data),

  getDryOffs: (params) => api.get('/repro/dryoffs/', { params }),
  createDryOff: (data) => api.post('/repro/dryoffs/', data),
  createDryOffBulk: (data) => api.post('/repro/dryoffs/bulk-create/', data),
  importDryOffsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/repro/dryoffs/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCalvings: (params) => api.get('/repro/calvings/', { params }),
  createCalving: (data) => api.post('/repro/calvings/', data),
  autoLinkCalvingCalves: (data = {}) => api.post('/repro/calvings/auto-link-calves/', data),
  importCalvingsExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    return api.post('/repro/calvings/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Cow timeline
  getCowTimeline: (cowId) => api.get(`/repro/cows/${cowId}/timeline/`),

  // Cow last service (days since last AI/service)
  getCowLastService: (cowId, params) => api.get(`/repro/cows/${cowId}/last_service/`, { params }),

  // Comprehensive reproduction reports and analytics
  getComprehensiveReproReport: (params) => api.get('/repro/reports/comprehensive/', { params }),
  exportComprehensiveReproReportExcel: (params) => api.get('/repro/reports/comprehensive/', {
    params: { ...(params || {}), export: 1 },
    responseType: 'blob',
  }),

  // Report: cows by days since last service
  getCowsByServiceDaysRange: (params) => api.get('/repro/reports/service-days-range/', { params }),
  getCalvingForecast: (params) => api.get('/repro/reports/calving-forecast/', { params }),
  exportCalvingForecastExcel: (params) => api.get('/repro/reports/calving-forecast/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportCalvingForecastPDF: (params) => api.get('/repro/reports/calving-forecast/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  getFertilityNeglectedMilkingReport: (params) => cachedGet('/repro/reports/fertility-neglected-milking/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportFertilityNeglectedMilkingExcel: (params) => api.get('/repro/reports/fertility-neglected-milking/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getCalvingInformationReport: (params) => cachedGet('/repro/reports/calving-information/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportCalvingInformationExcel: (params) => api.get('/repro/reports/calving-information/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getPretermCalvingRate: (params) => api.get('/repro/reports/preterm-calving-rate/', { params }),
  exportPretermCalvingRateExcel: (params) => api.get('/repro/reports/preterm-calving-rate/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportPretermCalvingRatePDF: (params) => api.get('/repro/reports/preterm-calving-rate/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),
  getHerdReproductionStatusSummary: (params) => api.get('/repro/reports/herd-status/summary/', { params }),
  getHerdReproductionStatusAnimals: (params) => api.get('/repro/reports/herd-status/animals/', { params }),
  exportHerdReproductionStatusAnimalsExcel: (params) => api.get('/repro/reports/herd-status/animals/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportCowsByServiceDaysRangeExcel: (params) => api.get('/repro/reports/service-days-range/', {
    // IMPORTANT: do NOT use `format=xlsx` here.
    // DRF reserves the `format` query param for renderer negotiation; when a renderer for xlsx
    // is not registered, it can return 404 before the view runs.
    // We use `export=1` which the backend view supports.
    params: { ...(params || {}), export: 1 },
    responseType: 'blob',
  }),
  getLegacyFertilityReport: (params) => api.get('/repro/reports/legacy-fertility/', { params }),
  exportLegacyFertilityReportExcel: (params) => api.get('/repro/reports/legacy-fertility/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  exportLegacyFertilityReportPDF: (params) => api.get('/repro/reports/legacy-fertility/', { params: { ...(params || {}), export: 'pdf' }, responseType: 'blob' }),

  // Pregnancy Checks
  getPregnancyChecks: (params) => api.get('/repro/pregnancy-checks/', { params }),
  createPregnancyCheck: (data) => api.post('/repro/pregnancy-checks/', data),
  downloadPregnancyChecksTemplate: () => api.get('/repro/pregnancy-checks/template/', { responseType: 'blob' }),
  importPregnancyChecksExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.validate_only) formData.append('validate_only', '1');
    if (options.import_batch) formData.append('import_batch', options.import_batch);
    if (options.download_pdf_report) formData.append('download_pdf_report', '1');
    return api.post('/repro/pregnancy-checks/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: options.download_pdf_report ? 'blob' : 'json',
    });
  },
  downloadPregnancyCheckImportReportPdf: (importBatch) => api.get('/repro/pregnancy-checks/import-report-pdf/', {
    params: { import_batch: importBatch },
    responseType: 'blob',
  }),
  getPregnancyCheckImportHistory: (params) => api.get('/repro/pregnancy-checks/import-history/', { params }),

  // Pregnancy cycles and standard 21-day reproduction analytics
  getPregnancyCycles: (params) => api.get('/repro/pregnancy-cycles/', { params }),
  getPregnancyCycle: (id) => api.get(`/repro/pregnancy-cycles/${id}/`),
  getPregnancyCycleAnalytics: (params) => api.get('/repro/pregnancy-cycles/analytics/', { params }),
  getPregnancyCycleRiskWindows: (params) => api.get('/repro/pregnancy-cycles/risk_windows/', { params }),
  getPregnancyCycleOutcomes: (params) => api.get('/repro/pregnancy-cycles/outcomes/', { params }),
  rebuildPregnancyCycle: (id) => api.post(`/repro/pregnancy-cycles/${id}/rebuild/`),
  resolvePregnancyCycle: (id, data) => api.post(`/repro/pregnancy-cycles/${id}/resolve/`, data),
  rebuildCowPregnancyCycles: (cowId) => api.post('/repro/pregnancy-cycles/rebuild_cow/', { cow_id: cowId }),
  exportPregnancyCycles: (format, params) => api.get('/repro/pregnancy-cycles/export/', {
    params: { ...(params || {}), format },
    responseType: 'blob',
  }),

  // Vet Visit Plans / Ultrasound planning
  getVisitPlans: (params) => api.get('/repro/visit-plans/', { params }),
  getVisitPlan: (id) => api.get(`/repro/visit-plans/${id}/`),
  getVisitPlanCandidates: (params) => api.get('/repro/visit-plans/candidates/', { params }),
  createVisitPlan: (data) => api.post('/repro/visit-plans/', data),
  addCowsToVisitPlan: (id, data) => api.post(`/repro/visit-plans/${id}/add-cows/`, data),
  createVisitSessionFromPlan: (id, data) => api.post(`/repro/visit-plans/${id}/create-session/`, data || {}),
  compareVisitPlan: (id, params) => api.get(`/repro/visit-plans/${id}/compare/`, { params }),
  exportVisitPlanExcel: (id, params) => api.get(`/repro/visit-plans/${id}/export-excel/`, { params, responseType: 'blob' }),
  getVisitReferrals: (params) => api.get('/repro/visit-referrals/', { params }),
  createVisitReferral: (data) => api.post('/repro/visit-referrals/', data),
  updateVisitReferral: (id, data) => api.patch(`/repro/visit-referrals/${id}/`, data),

  // Vet Visits
  getVisits: (params) => api.get('/repro/visits/', { params }),
  createVisit: (data) => api.post('/repro/visits/', data),
  updateVisit: (id, data) => api.patch(`/repro/visits/${id}/`, data),
  endVisit: (id) => api.post(`/repro/visits/${id}/end/`),
  getVisitSummary: (id) => api.get(`/repro/visits/${id}/summary/`),
  getVisitReport: (id) => api.get(`/repro/visits/${id}/report/`),
  exportVisitReportPDF: (id) => api.get(`/repro/visits/${id}/report/pdf/`, { responseType: 'blob' }),
  exportVisitExcel: (id) => api.get(`/repro/visits/${id}/export_excel/`, { responseType: 'blob' }),

  // Visit Items (fast entry)
  createVisitItem: (data) => api.post('/repro/visit-items/', data),
  getRevisitDue: (params) => api.get('/repro/visit-items/revisit-due/', { params }),
  exportRevisitDueExcel: (params) => api.get('/repro/visit-items/revisit-due/', { params: { ...(params || {}), export: 1 }, responseType: 'blob' }),

  // KPI
  getConceptionRate: (params) => api.get('/repro/kpi/conception-rate/', { params }),
  getReproOverview: (params) => api.get('/repro/kpi/overview/', { params }),

  // Dynamic policy & daily planner
  getReproPolicy: () => api.get('/repro/policy/'),
  updateReproPolicy: (data) => api.put('/repro/policy/', data),
  getDailyReproPlanner: (params) => api.get('/repro/planner/daily/', { params }),
  getDailyReproPlannerV2: (params) => api.get('/repro/planner/daily-v2/', { params }),

  // مرکز گزارش‌های قدیمی و گزارش‌ساز پویا دام‌یار
  getLegacyReportCenter: () => cachedGet('/reports/legacy-center/', { cache: { ttl: CACHE_TTL.MEDIUM } }),
  getLegacyReportEndpoint: (endpoint, params) => cachedGet(endpoint.replace(/^\/api/, ''), { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportLegacyReportEndpoint: (endpoint, params) => api.get(endpoint.replace(/^\/api/, ''), { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getLegacyAdvancedCatalog: () => api.get('/reports/legacy-advanced/'),
  runLegacyAdvancedReport: (endpoint, params, responseType) => api.get(endpoint, { params, ...(responseType ? { responseType } : {}) }),
  getDynamicReportCatalog: () => api.get('/reports/dynamic/'),
  runDynamicReport: (slug, params) => api.get(`/reports/dynamic/${slug}/`, { params }),
  getDynamicReportRuns: () => api.get('/reports/dynamic/runs/'),
  getDynamicReportSchedules: () => api.get('/reports/dynamic/schedules/'),
  createDynamicReportSchedule: (data) => api.post('/reports/dynamic/schedules/', data),
  getReportAnalyticsDashboard: () => api.get('/reports/analytics/dashboard/'),
  getReportCowDrilldown: (params) => api.get('/reports/analytics/cow-drilldown/', { params }),
  runDueReportSchedules: () => api.post('/reports/analytics/run-due-schedules/'),

  // انبار داده، شاخص‌های مدیریتی و تحلیل سازمانی
  getAdvancedAnalyticsDashboard: () => api.get('/reports/analytics/executive-dashboard/', { timeout: 900000 }),
  getAdvancedCowTimeline: (params) => api.get('/reports/analytics/cow-timeline/', { params }),
  getAdvancedCowRiskScore: (params) => api.get('/reports/analytics/cow-risk-score/', { params }),
  buildAdvancedAnalyticsSnapshot: (data) => api.post('/reports/analytics/build-snapshot/', data || {}, { timeout: 900000 }),
  getEnterpriseAnalyticsCatalog: () => api.get('/reports/enterprise/catalog/'),
  getSCCAnalytics: (params) => api.get('/reports/enterprise/scc/', { params }),
  getReproductionAnalytics: (params) => api.get('/reports/enterprise/reproduction/', { params }),
  getSemenGeneticsAnalytics: (params) => api.get('/reports/enterprise/semen/', { params }),
  getEconomicAnalytics: (params) => api.get('/reports/enterprise/economics/', { params }),
  getCowRiskScoreAnalytics: (params) => api.get('/reports/enterprise/risk-score/', { params }),

  // گزارش‌های خوراک معادل RPT
  getFeedLegacyReportMap: (params) => cachedGet('/reports/feed-legacy-rpt-map/', { params, cache: { ttl: CACHE_TTL.SHORT } }),
  exportFeedLegacyReportMapExcel: (params) => api.get('/reports/feed-legacy-rpt-map/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),

  // مرکز پیامک کاوه‌نگار
  getSMSSettings: () => api.get('/notifications/sms/settings/'),
  updateSMSSettings: (data) => api.put('/notifications/sms/settings/', data),
  sendTestSMS: (data) => api.post('/notifications/sms/test/', data),
  sendManualSMS: (data) => api.post('/notifications/sms/send/', data),
  getSMSLogs: (params) => api.get('/notifications/sms/logs/', { params }),
  retryFailedSMS: (id) => api.post(`/notifications/sms/logs/${id}/retry/`),

};


export const notificationsAPI = {
  getSMSSettings: () => api.get('/notifications/sms/settings/'),
  updateSMSSettings: (data) => api.put('/notifications/sms/settings/', data),
  sendTestSMS: (data) => api.post('/notifications/sms/test/', data),
  sendManualSMS: (data) => api.post('/notifications/sms/send/', data),
  getSMSLogs: (params) => api.get('/notifications/sms/logs/', { params }),
  getSMSTemplates: (params) => api.get('/notifications/sms/templates/', { params }),
  createSMSTemplate: (data) => api.post('/notifications/sms/templates/', data),
  retryFailedSMS: (id) => api.post(`/notifications/sms/logs/${id}/retry/`),
};


// API بخش‌های حرفه‌ای خوراک، خوراک گوساله و تحلیل شیر
export const feedProfessionalAPI = {
  getFeedItems: (params) => api.get('/feed-module/items/', { params }),
  createFeedItem: (data) => api.post('/feed-module/items/', data),
  getFeedLots: (params) => api.get('/feed-module/lots/', { params }),
  createFeedLot: (data) => api.post('/feed-module/lots/', data),
  getFeedInventorySummary: (params) => api.get('/feed-module/inventory-summary/', { params }),
  getRationFormulas: (params) => api.get('/feed-module/ration-formulas/', { params }),
  createRationFormula: (data) => api.post('/feed-module/ration-formulas/', data),
  updateRationFormula: (id, data) => api.patch(`/feed-module/ration-formulas/${id}/`, data),
  deleteRationFormula: (id) => api.delete(`/feed-module/ration-formulas/${id}/`),
  getRationFormulaItems: (formulaId, params) => api.get(`/feed-module/ration-formulas/${formulaId}/items/`, { params }),
  createRationFormulaItem: (formulaId, data) => api.post(`/feed-module/ration-formulas/${formulaId}/items/`, data),
  updateRationFormulaItem: (id, data) => api.patch(`/feed-module/ration-formula-items/${id}/`, data),
  deleteRationFormulaItem: (id) => api.delete(`/feed-module/ration-formula-items/${id}/`),
  getBaharbandAssignments: (params) => api.get('/feed-module/baharband-assignments/', { params }),
  createBaharbandAssignment: (data) => api.post('/feed-module/baharband-assignments/', data),
  updateBaharbandAssignment: (id, data) => api.patch(`/feed-module/baharband-assignments/${id}/`, data),
  getBaharbandFeedHistory: (params) => api.get('/feed-module/baharband-history/', { params }),
  getBaharbandDeliveries: (params) => api.get('/feed-module/baharband-deliveries/', { params }),
  createBaharbandDelivery: (data) => api.post('/feed-module/baharband-deliveries/', data),
  previewDailyFastFeed: (params) => api.get('/feed-module/daily-fast-preview/', { params }),
  recalcDailyFastFeed: (data) => api.post('/feed-module/daily-fast-preview/', data),
  saveDailyFastFeed: (data) => api.post('/feed-module/daily-fast-bulk/', data),
  getFeedManagementReport: (params) => api.get('/feed-module/management-report/', { params }),
  exportFeedManagementReport: (params) => api.get('/feed-module/management-report/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getFeedPerformanceAnalytics: (params) => api.get('/feed-module/performance-analytics/', { params }),
  exportFeedPerformanceAnalytics: (params) => api.get('/feed-module/performance-analytics/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getFeedAlertDashboard: (params) => api.get('/feed-module/alert-dashboard/', { params }),
  exportFeedAlertDashboard: (params) => api.get('/feed-module/alert-dashboard/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getExcelImportScopes: () => api.get('/feed-module/excel-import/scopes/'),
  downloadExcelImportTemplate: (scope) => api.get(`/feed-module/excel-import/template/${scope}/`, { responseType: 'blob' }),
  startExcelImportJob: (scope, file) => {
    const formData = new FormData();
    formData.append('scope', scope);
    formData.append('file', file);
    return api.post('/feed-module/excel-import/jobs/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
  },
  getExcelImportJob: (jobId) => api.get(`/feed-module/excel-import/jobs/${jobId}/`),
  downloadExcelImportReport: (jobId) => api.get(`/feed-module/excel-import/jobs/${jobId}/report/`, { responseType: 'blob' }),
};

export const calfFeedAPI = {
  getAgeIntake: (params) => api.get('/feed-module/calf-age-intake/', { params }),
  createAgeIntake: (data) => api.post('/feed-module/calf-age-intake/', data),
  deleteAgeIntake: (id) => api.post('/feed-module/calf-age-intake/', { action: 'delete', id }),
  exportAgeIntake: (params) => api.get('/feed-module/calf-age-intake/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getAgeTargets: (params) => api.get('/feed-module/calf-age-targets/', { params }),
  saveAgeTargets: (targets) => api.post('/feed-module/calf-age-targets/', { targets }),
};

export const milkFeedAPI = {
  getMilkBaharbandPerformance: (params) => api.get('/milk-baharband-performance/', { params }),
  exportMilkBaharbandPerformance: (params) => api.get('/milk-baharband-performance/', { params: { ...(params || {}), export: 'xlsx' }, responseType: 'blob' }),
  getMilkSalesDailyRecords: (params) => api.get('/milk-herd-daily-records/', { params }),
  downloadIndividual10DayTemplate: () => api.get('/milk-individual-10day/template/', { responseType: 'blob' }),
  importIndividual10DayExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/milk-individual-10day/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export default api;


export const systemAPI = {
  getHealth: () => api.get('/system/health/'),
  getRuntime: () => api.get('/system/runtime/'),
  getNetworkDiagnostics: () => api.get('/system/network-diagnostics/', { timeout: API_TIMEOUTS.READ }),
};
